/**
 * Một chỗ duy nhất gọi máy chủ. Không `fetch` rải rác trong các màn hình.
 *
 * Hai điều quan trọng ở tầng này:
 *   1. Mọi lỗi từ máy chủ đều có câu tiếng Việt (`error.message`) — tầng này lấy đúng câu đó ra,
 *      không tự bịa câu mới. Nếu mạng chết thì mới tự nói: "Không có mạng".
 *   2. POST tạo lịch tự gắn `Idempotency-Key`. Lễ tân bấm hai lần vì máy chậm là chuyện thường,
 *      và hai lần bấm không được thành hai lịch.
 */

import type { BoTinhNang, DongTho, MaDongNhap, TinhNang, TraLoiDangKy } from '@sapa/shared'
import { chuanHoaSdtVn } from '@sapa/shared'
import type { BaoKhach } from './bao-khach.ts'
import type { TinhTrangChuanBi } from './chuan-bi-tiem.ts'
import type { ThanGuiPhanHoi } from './phan-hoi-kem.ts'
import type { ThanSoDuCu } from './so-du-cu.ts'
import { T as CHU } from './text/vi.ts'
import { GIOI_HAN_GET_MS, GIOI_HAN_POST_MS, GIOI_HAN_TAI_LEN_MS } from './tu-phuc-hoi.ts'

export interface GoiYGio {
  startAt: string
  startMin: number
}

export interface ApiError {
  code: string
  message: string
  details?: {
    goiY?: GoiYGio[]
    lichVuong?: LichVuong[]
    daCo?: SoDuCuDaCo[]
    soKhac?: number
    /** Lỗi `DUPLICATE` của thêm / sửa khách: hồ sơ đã có số này (ADR-089). */
    id?: string
    ten?: string
    /** Lỗi `KHACH_DA_GOP` (STT 2.5): hồ sơ còn lại sau khi gộp — màn hình mở thẳng hồ sơ đó. */
    giuId?: string
    /** `OFFLINE` do HẾT GIỜ (không phải rớt ngay): yêu cầu có thể đã tới máy chủ — POST thì "chưa chắc đã lưu". */
    hetGio?: boolean
    /** Lỗi `KHONG_GOP_DUOC` (STT 2.5): mã lý do (bo-co-tien, zalo-khac…). */
    lyDo?: string
    /** Lỗi `TIEM_SE_NGUNG_NHAN` (v0.22.3 B5): các vế sẽ mất (`nguoi-nhan-khach` · `dich-vu` · `gio-mo-cua`). */
    mat?: string[]
    /** `VALIDATION` đòi (hoặc chê) MÃ ĐĂNG NHẬP gõ lại: đổi tài khoản nhận cọc, đổi số đăng nhập của chính mình (v0.22.3 B4). */
    canMa?: boolean
    /** v0.23.1 (ADR-123): việc đang cần mã (`cap-chu`, `dat-ma-chu`, …) — để thử/nhật ký, giao diện dùng CÂU của máy chủ. */
    viec?: string
    /** Lỗi `KHONG_CON_NGUOI_NHAN` (v0.22.3, L-N1): chi nhánh mà người này là người nhận khách cuối cùng. */
    chiNhanhIds?: string[]
  }
}

/**
 * Một lần nhập số dư cũ trước đây của khách (lỗi `OPENING_EXISTS`, STT 2.3): quầy hỏi lại "Nhập thêm nữa?".
 * Tiền là chuỗi số nguyên đồng (ADR-026); không có lý do, không có số điện thoại.
 */
export type SoDuCuDaCo =
  | {
      loai: 'goi'
      /** yyyy-MM-dd theo giờ VN. */
      ngay: string
      ten: string
      soBuoi: number
      giaTriVnd: string
      tang: boolean
    }
  | { loai: 'vi'; ngay: string; cashVnd: string; bonusVnd: string }

/** Lịch còn sống của KTV trong những ngày định cho nghỉ (lỗi `STAFF_BUSY`, F06 — từ STT 3.4 không còn ném). */
export interface LichVuong {
  code: string
  batDau: string
  khach: string | null
}

/**
 * STT 3.4 (ADR-092): một lịch VƯỚNG khi cho nghỉ — trả kèm 201 của `POST …/ngay-nghi`. Cùng định nghĩa với
 * `/v1/admin/lich-vuong` và `DayBoard.nghi[].lichCanXuLy` (một hàm `dsLichVuong` ở máy chủ). `batDau` có offset.
 */
export interface LichVuongNghi {
  appointmentId: string
  code: string
  batDau: string
  khach: string | null
  branchId: string
  /** BK-2 (ADR-096): `nghi-viec` · `ngoai-ca` · `nghi`; K2 (ADR-111): `tiem-nghi` = tiệm đóng cửa ngày đó. Thiếu = `nghi` (bản máy chủ cũ). */
  lyDo?: 'nghi' | 'nghi-viec' | 'ngoai-ca' | 'tiem-nghi'
}

/**
 * BK-2 (ADR-096): phản hồi của đổi ca / cho nghỉ việc / đổi giờ mở cửa — đã LƯU, kèm các lịch còn cần xử lý
 * (tối đa 50 dòng; `soLichVuong` là tổng). Máy chủ cũ không trả → coi là rỗng.
 */
export interface LichVuongSauLuu {
  soLichVuong?: number
  lichVuong?: LichVuongNghi[]
}

/** Một người đã nghỉ việc / có lịch nằm ngoài ca hoặc ngoài giờ mở cửa trong ngày đang xem (`GET /v1/admin/day`.ngoaiCa). */
export interface NgoaiCaTrongNgay {
  staffId: string
  ten: string
  lyDo: 'nghi-viec' | 'ngoai-ca' | 'tiem-nghi'
  lichCanXuLy: string[]
}

/** Một người nghỉ trong ngày đang xem + id các lịch của người đó còn cần xử lý (`GET /v1/admin/day`.nghi). */
export interface NghiTrongNgay {
  ngayNghiId: string
  staffId: string
  ten: string
  lichCanXuLy: string[]
}

/** Một dòng của `GET /v1/admin/lich-vuong` — ghép với `DayBoard.appointments` theo `appointmentId` để có tên khách / dịch vụ. */
export interface DongLichVuong {
  appointmentId: string
  staffId: string
  startMin: number
  /** Vì sao vướng (BK-2; K2 thêm `tiem-nghi`); thiếu = người nghỉ. */
  lyDo?: 'nghi' | 'nghi-viec' | 'ngoai-ca' | 'tiem-nghi'
  /**
   * `chuyen` = một dịch vụ, còn ở tương lai → chuyển người được (kể cả lịch chờ nhận / chờ cọc, B7); `cho-duyet` = chờ nhận / chờ cọc
   * mà đã tới / qua giờ hẹn (chỉ mở lịch để từ chối / huỷ); `nhieu-dich-vu` = chưa chuyển được.
   */
  xuLy: 'chuyen' | 'cho-duyet' | 'nhieu-dich-vu'
  /** B7: trạng thái lịch — `pending` chờ nhận · `awaiting_deposit` chờ cọc · `booked` đã nhận. Thiếu = đã nhận (máy chủ cũ). */
  trangThai?: 'booked' | 'pending' | 'awaiting_deposit'
  /** Người rảnh đúng giờ này và làm được dịch vụ này, người ít việc nhất lên đầu. Rỗng khi không ai. */
  ungVien: { staffId: string; ten: string }[]
}

export class ApiFailure extends Error {
  readonly code: string
  readonly messageVi: string
  readonly status: number
  /**
   * Ba giờ gần nhất khi khe vừa bị người khác lấy mất. Lễ tân đang nghe điện thoại cần **giờ để
   * đọc cho khách**, không cần một câu báo lỗi — nên gợi ý phải đi cùng lỗi tới tận giao diện.
   */
  readonly goiY: GoiYGio[]
  constructor(status: number, err: ApiError) {
    super(`${err.code}: ${err.message}`)
    this.code = err.code
    this.messageVi = err.message
    this.status = status
    this.goiY = err.details?.goiY ?? []
    this.lichVuong = err.details?.lichVuong ?? []
    this.daCo = err.details?.daCo ?? []
    this.soKhac = err.details?.soKhac ?? 0
    this.khachTrung =
      err.code === 'DUPLICATE' &&
      typeof err.details?.id === 'string' &&
      typeof err.details?.ten === 'string'
        ? { id: err.details.id, ten: err.details.ten }
        : null
    this.hoSoGiuId =
      err.code === 'KHACH_DA_GOP' && typeof err.details?.giuId === 'string'
        ? err.details.giuId
        : null
    this.hetGio = err.details?.hetGio === true
    this.tenNguoiCuoi =
      err.code === 'KHONG_CON_NGUOI_NHAN' && typeof err.details?.ten === 'string'
        ? err.details.ten
        : null
    this.chiNhanhNguoiCuoi =
      err.code === 'KHONG_CON_NGUOI_NHAN' && Array.isArray(err.details?.chiNhanhIds)
        ? err.details.chiNhanhIds.filter((x): x is string => typeof x === 'string')
        : []
    this.matKhiLuu =
      err.code === 'TIEM_SE_NGUNG_NHAN' && Array.isArray(err.details?.mat) ? err.details.mat : []
    this.canMa = err.details?.canMa === true
  }
  /** Máy chủ đòi / chê mã đăng nhập vừa gõ — giao diện hiện ô mã ngay tại chỗ, không đẩy lỗi lên đầu trang. */
  readonly canMa: boolean
  /** Lỗi `KHONG_CON_NGUOI_NHAN` (v0.22.3 B1): tên người nhận khách cuối cùng — để tờ "Cả tiệm nghỉ hôm nay?" gọi đúng tên. */
  readonly tenNguoiCuoi: string | null
  /** Lỗi `KHONG_CON_NGUOI_NHAN` (L-N1): chi nhánh máy chủ báo là hết người nhận khách; `[]` ở mọi lỗi khác / máy chủ cũ. */
  readonly chiNhanhNguoiCuoi: string[]
  /** Lỗi `TIEM_SE_NGUNG_NHAN` (v0.22.3 B5): các vế tiệm sẽ mất nếu lưu; `[]` ở mọi lỗi khác. */
  readonly matKhiLuu: string[]
  /**
   * Hết giờ chờ (đợt 3 · 3.1). Bên gọi vẫn giữ khoá chống lặp như mọi lỗi `OFFLINE`; câu nói đã tự ghi
   * "chưa chắc đã lưu" cho POST. Không bao giờ tự gửi lại.
   */
  readonly hetGio: boolean
  /** Hồ sơ đã có cùng số khi thêm / sửa khách (lỗi `DUPLICATE`) — để mở thẳng hồ sơ đó. */
  readonly khachTrung: { id: string; ten: string } | null
  /** Hồ sơ khách đã được gộp vào hồ sơ này (lỗi `KHACH_DA_GOP`) — mở hồ sơ còn lại. `null` = không phải lỗi đó. */
  readonly hoSoGiuId: string | null
  /** Các lần nhập số dư cũ trước đây (tối đa 3, mới nhất trước) khi lỗi là `OPENING_EXISTS`. */
  readonly daCo: SoDuCuDaCo[]
  /** Số lần nhập cùng loại còn lại ngoài các dòng trong `daCo`. */
  readonly soKhac: number
  /** Lịch vướng khi cho KTV nghỉ — quầy cần biết đổi người cho lịch nào. */
  readonly lichVuong: LichVuong[]
}

const BASE = import.meta.env.VITE_API_URL ?? 'http://127.0.0.1:3010'

/** Địa chỉ đầy đủ của một đường dẫn máy chủ (để mở trang HTML do máy chủ phát, vd điều khoản dịch vụ — 5.4). */
export function duongDanMayChu(duong: string): string {
  return `${BASE}${duong}`
}

/** Nơi giữ token của phiên đang mở. Xem `luuToken` để biết vì sao lưu ở `localStorage`. */
const KHOA_TOKEN = 'sapa.token'

export function docToken(): string | null {
  try {
    return localStorage.getItem(KHOA_TOKEN)
  } catch {
    // Chế độ riêng tư của trình duyệt có thể ném lỗi. Không có token thì về màn đăng nhập,
    // chứ không được làm sập ứng dụng.
    return null
  }
}

/**
 * Lưu token ở `localStorage`.
 *
 * Vì sao không dùng cookie: web quầy và máy chủ ở hai gốc khác nhau (Vite 5173 / API 3000, và khi
 * chạy thật thì cũng có thể khác tên miền), nên cookie thành cuộc chiến `SameSite`. Đổi lại,
 * `localStorage` đọc được bằng JavaScript nên một lỗ XSS lấy được token — vì vậy chuỗi hiển thị
 * đều đi qua React (tự escape) và không có `dangerouslySetInnerHTML` ở đâu trong ứng dụng này.
 */
export function luuToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(KHOA_TOKEN)
    else localStorage.setItem(KHOA_TOKEN, token)
  } catch {
    // Không lưu được thì phiên chỉ sống tới khi đóng tab. Vẫn làm việc được.
  }
}

/** Ai cần biết "phiên vừa chết" thì đăng ký ở đây — chủ yếu là màn hình gốc để quay về đăng nhập. */
let khiPhienChet: (() => void) | null = null
export function dangKyKhiPhienChet(f: (() => void) | null): void {
  khiPhienChet = f
}

/**
 * 401 chỉ là "phiên chết" khi yêu cầu CÓ gửi token. Đăng nhập sai (chưa có token) cũng trả 401 — trước
 * 27/09 nó bị coi là phiên chết nên màn đăng nhập hiện thêm "Phiên làm việc đã kết thúc" (lỗi L1).
 */
export function laPhienChet(status: number, coToken: boolean): boolean {
  return status === 401 && coToken
}

/**
 * Hạn chót của một yêu cầu (đợt 3 · 3.1): GET 15 giây, POST 20 giây — quá thì coi như mất mạng (`OFFLINE`).
 * Tự dựng bằng `AbortController` + `setTimeout` (không `AbortSignal.timeout`): máy tablet cũ của quầy có thể chạy
 * Safari < 16 không có hàm đó, và hẹn giờ thường thì test dùng đồng hồ giả được.
 *
 * POST hết giờ: máy chủ CÓ THỂ đã ghi. Nói thật "chưa chắc đã lưu", KHÔNG tự gửi lại; mã vẫn là `OFFLINE` nên
 * các tờ giữ khoá chống lặp (`useKhoaIdem`) giữ nguyên khoá và bấm lại gửi ĐÚNG khoá cũ.
 */
/**
 * POST nhưng chỉ ĐỌC (đi POST để số điện thoại không nằm trong nhật ký truy cập): hết giờ thì chẳng có gì "chưa chắc
 * đã lưu" — nói thế làm người dùng tưởng đã ghi một thứ gì đó (vòng sửa 3.1).
 */
const POST_CHI_DOC = ['/v1/admin/customers/tim', '/v1/admin/lich-hen']

function laLenhDoc(method: string, path: string): boolean {
  if (method === 'GET' || method === 'HEAD') return true
  const duong = path.split('?')[0] ?? path
  return POST_CHI_DOC.includes(duong)
}

function gioiHanCua(method: string, path: string, gioiHanMs?: number): number {
  if (gioiHanMs !== undefined) return gioiHanMs
  return laLenhDoc(method, path) ? GIOI_HAN_GET_MS : GIOI_HAN_POST_MS
}

/** `OFFLINE`: rớt ngay / hết giờ. Lệnh GHI hết giờ thì nói thật "chưa chắc đã lưu" (máy chủ có thể đã ghi). */
function loiMatMang(hetGio: boolean, method: string, path: string): ApiFailure {
  const laDoc = laLenhDoc(method, path)
  if (hetGio && !laDoc) {
    return new ApiFailure(0, {
      code: 'OFFLINE',
      message: CHU.chuaChacDaLuu,
      details: { hetGio: true },
    })
  }
  return new ApiFailure(0, {
    code: 'OFFLINE',
    message: 'Không có mạng. Bạn thử lại nhé.',
    ...(hetGio ? { details: { hetGio: true } } : {}),
  })
}

async function goi<T>(path: string, init?: RequestInit, gioiHanMs?: number): Promise<T> {
  const token = docToken()
  const method = (init?.method ?? 'GET').toUpperCase()
  const dieuKhien = new AbortController()
  let hetGio = false
  const hen = setTimeout(
    () => {
      hetGio = true
      dieuKhien.abort()
    },
    gioiHanCua(method, path, gioiHanMs),
  )
  let res: Response
  let text: string
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      signal: dieuKhien.signal,
      headers: {
        'content-type': 'application/json',
        ...(token === null ? {} : { authorization: `Bearer ${token}` }),
        ...(init?.headers ?? {}),
      },
    })
    // Thân phản hồi cũng nằm dưới hạn chót: mạng chết giữa chừng lúc đang tải thân thì `text()` treo y như `fetch`.
    text = await res.text()
  } catch {
    throw loiMatMang(hetGio, method, path)
  } finally {
    clearTimeout(hen)
  }
  let body: Record<string, unknown> = {}
  if (text !== '') {
    try {
      body = JSON.parse(text) as Record<string, unknown>
    } catch {
      // Máy chủ trung gian (Caddy 413 / 502) trả chữ thường, không phải JSON: đừng để thợ thấy "Không nối được máy chủ".
      body = {
        error: { code: 'INTERNAL', message: 'Hệ thống đang lỗi. Bạn thử lại sau ít phút nhé.' },
      }
    }
  }
  if (!res.ok) {
    const err = (body.error ?? { code: 'INTERNAL', message: 'Hệ thống đang lỗi.' }) as ApiError
    /*
     * 401 nghĩa là phiên đã chết (hết hạn, hoặc chủ tiệm thu hồi). Phải quay về màn đăng nhập
     * NGAY, chứ không phải hiện một câu lỗi đỏ rồi để lễ tân bấm tiếp mà không hiểu vì sao
     * chẳng cái gì hoạt động.
     */
    if (laPhienChet(res.status, token !== null)) {
      luuToken(null)
      khiPhienChet?.()
    }
    throw new ApiFailure(res.status, err)
  }
  return body as T
}

export interface ToiLaAi {
  toi: {
    id: string
    ten: string
    vai: string
    tenVai: string
    phaiDoiMa: boolean
    /** Có dịch vụ được gán → hiện "Việc của tôi" (phản ánh 25/09). Máy chủ cũ không trả → coi như không. */
    lamDichVu?: boolean
  }
  quyen: string[]
  congTac: { ktvXemSdt: boolean; ktvXemAnh?: boolean; quanLyXemDoanhThu: boolean }
  tiem: string
  /** Nhận diện cho khung giao diện (ADR-036). */
  tenTiem: string
  maTiem: string
  nganh: string
  /**
   * v0.23.3 (ADR-124): cờ máy chủ `ANH_CUA_HANG_BAT` cho tiệm này + ảnh đang dùng (đường dẫn tương đối `/v1/public/anh-cua-hang/<32 hex>.webp`).
   * Máy chủ cũ không trả → coi như tắt (thẻ "Ảnh bìa và logo" ẩn).
   */
  anhCuaHang?: { bat: boolean; bia: string | null; logo: string | null }
  /** Mã của chủ tiệm vừa được đặt lại qua Zalo / bởi SAPA (7 ngày, chỉ chủ + quản lý). Máy chủ cũ không trả. */
  datLaiMa?: { id: string; cach: 'zalo' | 'sapa'; ten: string; luc: string }[]
  /**
   * v0.23.1 (ADR-123): việc NGƯỜI TRONG TIỆM làm tới một chủ trong 7 ngày (giao / bỏ vai, cho nghỉ / đi làm lại, đặt lại mã, đổi số),
   * trừ việc do chính phiên này làm. Chủ + quản lý. Trường RIÊNG (không chung `datLaiMa`) để bản web cũ bỏ qua. Máy chủ cũ không trả.
   */
  doiChuGanDay?: {
    id: string
    viec: 'cap-chu' | 'bo-chu' | 'cho-nghi-chu' | 'di-lam-lai-chu' | 'dat-ma-chu' | 'doi-so-chu'
    ai: string
    ten: string
    luc: string
    /** v0.23.2 (B-N4): chính phiên này làm (dòng chỉ hiện sau 10 phút) -> "Bạn vừa…". Máy chủ cũ không trả. */
    laToi?: boolean
  }[]
  /**
   * v0.23.2 (B-V2): việc NGƯỜI TRONG TIỆM làm tới một Quản lý / Lễ tân trong 7 ngày (thêm, giao vai, đặt mã hộ, đổi số, cho đi làm lại).
   * Chủ + quản lý. Trường RIÊNG (bản web cũ bỏ qua). Máy chủ cũ không trả.
   */
  doiQuanLyGanDay?: {
    id: string
    viec: 'them' | 'giao-vai' | 'dat-ma' | 'doi-so' | 'di-lam-lai'
    vai: 'manager' | 'cashier'
    ai: string
    ten: string
    luc: string
    laToi: boolean
  }[]
  /**
   * Ứng dụng đặt lịch trên Zalo đã chạy thật (`ZALO_MINIAPP_LIVE=1` ở máy chủ). Thiếu / máy chủ cũ = CHƯA — nói thật:
   * không in tờ QR, không chèn link chết vào tin nhắc (kế hoạch 10/2026, 1.5).
   */
  miniAppLive?: boolean
  /**
   * ADR-101: chi nhánh được giao cho CHÍNH người đăng nhập (`staff_branch`), chi nhánh chính trước. Tiện cho giao diện (chọn
   * mặc định, lễ tân / KTV chỉ chọn trong chi nhánh được giao), không phải quyền. Máy chủ cũ không trả → coi như rỗng.
   */
  chiNhanhCuaToi?: string[]
  /**
   * Gộp hồ sơ khách đang mở (`GOP_KHACH_BAT`, mặc định bật). Tắt → máy chủ đã bỏ quyền `gop-khach` khỏi `quyen` nên nút
   * "Gộp với hồ sơ khác" tự ẩn; cờ này chỉ để giao diện nói thật nếu cần. Máy chủ cũ không trả.
   */
  gopKhachBat?: boolean
}

export interface HoTro {
  /** Số người vận hành SAPA nhận hỗ trợ (dạng hiển thị), `null` = chưa cấu hình. */
  sdt: string | null
  /** Link nhắn Zalo cho SAPA (số hỗ trợ, hoặc OA của SAPA). Máy chủ cũ không trả. */
  zalo?: string | null
  /** Chủ tiệm tự lấy lại mã bằng mã gửi qua Zalo (đợt 3) — chỉ khi mẫu ZBS đã duyệt. */
  otp: boolean
}

/** Một tiệm mang số điện thoại đã xác nhận mã Zalo (SEC-6) — người dùng tự chọn tiệm cần đặt lại mã. `moNgay` = YYYY-MM-DD giờ VN. */
export interface TiemDatMa {
  id: string
  ten: string
  maTiem: string
  moNgay: string
}

export type KetQuaDangNhap =
  | {
      token: string
      hetHanLuc: string
      toi: ToiLaAi['toi']
      /** Máy chủ cũ (trước 27/09) không trả. */
      tiem?: { id: string; ten: string }
    }
  | { chonTiem: TiemDeChon[] }

/** Một tiệm trong màn "Bạn vào tiệm nào?". `ma`, `tenVai`: máy chủ cũ không trả. */
export interface TiemDeChon {
  id: string
  ten: string
  ma?: string
  tenVai?: string
}

/** Các đường dẫn xác thực. Không cần token (trừ `toi`/`doiMa`/`dangXuat`). */
export const auth = {
  /** Tự mở tiệm (ADR-053, ADR-087): chủ tiệm gửi kèm mã 6 số TỰ ĐẶT; máy chủ chỉ trả mã tiệm + số, không trả mã. */
  moTiem: (input: {
    tenTiem: string
    nganh: string
    tenChu: string
    sdt: string
    ma: string
    traLoi: TraLoiDangKy
    /** 5.4: chỉ gửi khi chủ tiệm đã tích ô đồng ý Điều khoản (cờ bật ở máy chủ). */
    dongYDieuKhoan?: true
  }) =>
    goi<{ tiem: string; sdt: string }>('/v1/public/dang-ky-tiem', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /**
   * SĐT + mã là đủ (ADR-071); `tiem` = id tiệm máy đã nhớ / mã trong link. Số làm ở nhiều tiệm cùng một mã
   * → máy chủ trả `chonTiem` (không token), người dùng chọn rồi gửi lại kèm `tiem`.
   */
  dangNhap: (input: { tiem?: string; sdt: string; ma: string; thietBi?: string }) =>
    goi<KetQuaDangNhap>('/v1/auth/dang-nhap', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /** Số hỗ trợ của SAPA (màn "Quên mã") + đã bật lấy lại mã qua Zalo chưa. */
  hoTro: () => goi<HoTro>('/v1/public/ho-tro'),
  /** 5.4: form mở tiệm có đòi ô tích đồng ý Điều khoản dịch vụ không (cờ ở máy chủ). Chưa có máy chủ mới → `bat: false`. */
  dieuKhoanCuaHang: () =>
    goi<{ bat: boolean; phienBan?: string; ngay?: string; duongDan?: string }>(
      '/v1/public/dieu-khoan-cua-hang',
    ),
  /** Chủ tiệm quên mã (ADR-072): xin mã qua Zalo → xác nhận → đặt mã mới. Trả lời "ok" như nhau cho mọi số. */
  quenMaGui: (sdt: string) =>
    goi<{ ok: boolean }>('/v1/public/quen-ma/gui', {
      method: 'POST',
      body: JSON.stringify({ sdt }),
    }),
  quenMaXacNhan: (sdt: string, ma: string) =>
    goi<{ phieu: string; tiem: string[]; dsTiem: TiemDatMa[] }>('/v1/public/quen-ma/xac-nhan', {
      method: 'POST',
      body: JSON.stringify({ sdt, ma }),
    }),
  /** SEC-6: `tiem` = các tiệm người dùng CHỌN đặt lại (bắt buộc khi số là chủ ≥ 2 tiệm; tiệm không chọn giữ mã cũ). */
  quenMaDatMa: (phieu: string, maMoi: string, tiem?: string[]) =>
    goi<{ ok: boolean; soTiem: number; conLai: number }>('/v1/public/quen-ma/dat-ma', {
      method: 'POST',
      body: JSON.stringify({ phieu, maMoi, ...(tiem === undefined ? {} : { tiem }) }),
    }),
  toi: () => goi<ToiLaAi>('/v1/auth/toi'),
  dangXuat: () => goi<{ ok: boolean }>('/v1/auth/dang-xuat', { method: 'POST' }),
  doiMa: (maCu: string, maMoi: string) =>
    goi<{ ok: boolean }>('/v1/auth/doi-ma', {
      method: 'POST',
      body: JSON.stringify({ maCu, maMoi }),
    }),
}

/** Loại tin ở chuông quầy (0028 + 0032 + 0035 + 0038). */
export type LoaiTinQuay =
  | 'lich_moi'
  | 'khach_huy'
  | 'xin_cho'
  | 'cho_duyet'
  | 'chua_xac_nhan'
  | 'khach_doi_gio'
  | 'khach_nhan'
  | 'cho_coc'
  | 'het_coc'
  // 0065 (KH-GĐ2 7.2.4, 7.3.4): CHỈ chủ tiệm — hoá đơn bị huỷ · két lệch.
  | 'hoa_don_huy'
  | 'ket_lech'
  // 0095 (K8): lịch chờ duyệt tự huỷ vì tiệm quên duyệt (chủ / lễ tân / quản lý; thợ không thấy).
  | 'het_duyet'

/** Một dòng "Cần nhắc" (N1): tin soạn sẵn + số để mở Zalo (chỉ khi người xem được xem số). */
export interface DongCanNhac {
  appointmentId: string
  code: string
  customerId: string | null
  batDau: string
  dichVu: string
  khach: string | null
  sdt: string | null
  sdtZalo: string | null
  trangThai: string
  daNhac: boolean
  daXacNhan: boolean
  tin: string
}

/** Một lần đến của khách (F05): thẻ khách → "Các lần đến". */
export interface LanDen {
  appointmentId: string
  code: string
  batDau: string
  trangThai: string
  dichVu: string[]
  tho: string[]
  daThuVnd: string | null
}
/**
 * Tải một tệp CSV có xác thực về máy (kèm token — không mở được bằng liên kết trần). Dùng chung cho tệp doanh thu tháng
 * (KH-GĐ2 5.4) và tệp khách (kế hoạch 10/2026 5.5). Tên tệp lấy từ `Content-Disposition` của máy chủ, thiếu thì `tenDuPhong`.
 */
async function taiTepVe(duong: string, tenDuPhong: string): Promise<void> {
  const token = docToken()
  let res: Response
  try {
    res = await fetch(`${BASE}${duong}`, {
      headers: token === null ? {} : { authorization: `Bearer ${token}` },
    })
  } catch {
    throw new ApiFailure(0, { code: 'OFFLINE', message: 'Không có mạng. Bạn thử lại nhé.' })
  }
  if (!res.ok) {
    const text = await res.text()
    let body: { error?: ApiError } = {}
    try {
      body = text.startsWith('{') ? (JSON.parse(text) as { error?: ApiError }) : {}
    } catch {
      // Thân lỗi hỏng → câu chung bên dưới.
    }
    if (laPhienChet(res.status, token !== null)) {
      luuToken(null)
      khiPhienChet?.()
    }
    throw new ApiFailure(
      res.status,
      body.error ?? { code: 'INTERNAL', message: 'Hệ thống đang lỗi.' },
    )
  }
  const ten = /filename="([A-Za-z0-9._-]{1,80})"/.exec(
    res.headers.get('content-disposition') ?? '',
  )?.[1]
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = ten ?? tenDuPhong
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export const khachApi = {
  lichSu: (id: string) => goi<{ lanDen: LanDen[] }>(`/v1/admin/khach/${id}/lich-su`),
  /** Kế hoạch 10/2026 5.5 (chỉ chủ tiệm): tải CSV toàn bộ khách + số dư ví + gói còn buổi về máy. */
  taiDuLieu: (): Promise<void> => taiTepVe('/v1/admin/khach/xuat', 'khach-sapa.csv'),
}

/** Báo cáo doanh thu (F09): khoá nhóm `''` = không ghi thợ / dòng khác; `'san-pham'` = bán lẻ. */
export interface BaoCaoDoanhThu {
  tu: string
  den: string
  theo: 'ngay' | 'tho' | 'dich-vu'
  tongVnd: string
  tongGoiVnd: string
  soHoaDon: number
  /**
   * Sổ tiền theo chi nhánh (v0.24.3). Tuỳ chọn vì máy chủ / bản cũ không trả: thiếu = `false` (màn y như cũ).
   * Số trên màn luôn là của CẢ TIỆM; `chiNhanh` chỉ để tách khi `nhieuChiNhanh`.
   */
  nhieuChiNhanh?: boolean
  chiNhanh?: ChiNhanhBaoCao[]
  dong: {
    khoa: string
    ten: string
    tienVnd: string
    goiVnd: string
    soDong: number
    /** Chỉ cách xem "Thợ" khi nhiều chi nhánh: phần của thợ ở từng chi nhánh CỦA HOÁ ĐƠN. */
    chiNhanh?: PhanChiNhanhBaoCao[]
  }[]
}
export interface ChiNhanhBaoCao {
  khoa: string
  ten: string
  daNgung: boolean
  tienVnd: string
  goiVnd: string
  soHoaDon: number
}
export interface PhanChiNhanhBaoCao {
  khoa: string
  ten: string
  tienVnd: string
  goiVnd: string
}
export const baoCao = {
  doanhThu: (tu: string, den: string, theo: BaoCaoDoanhThu['theo']) =>
    goi<BaoCaoDoanhThu>(`/v1/admin/bao-cao/doanh-thu?tu=${tu}&den=${den}&theo=${theo}`),
  /** KH-GĐ2 5.4: tải tệp CSV của một tháng về máy (kèm token — không mở được bằng liên kết trần). */
  taiTepThang: (thang: string): Promise<void> =>
    taiTepVe(`/v1/admin/bao-cao/xuat?thang=${encodeURIComponent(thang)}`, `bao-cao-${thang}.csv`),
}

/** Cài đặt của tiệm (0031): chế độ gọn + đặt lịch. */
export type { BoTinhNang, TinhNang }

export interface CaiDatTiem {
  /** Công tắc tính năng của tiệm (ADR-074). Máy chủ chặn đường của tính năng tắt; ở đây chỉ để ẩn. */
  tinhNang: BoTinhNang
  datLich: {
    tuDatLich: boolean
    duyetTay: boolean
    mocNhac: 'truoc_24h' | 'sang_cung_ngay'
    hanXacNhanPhut: number
  }
}
/** Quyền của thợ (công tắc tiệm, chỉ chủ tiệm đổi). 0042: ảnh tách khỏi số điện thoại. */
export interface QuyenTho {
  ktvXemSdt: boolean
  ktvXemAnh: boolean
  /** Phản ánh 25/09: hai công tắc máy chủ có từ B1 mà chưa có chỗ bật/tắt. */
  quanLyXemDoanhThu: boolean
  /** KH-GĐ2 7.1 (ADR-078): thợ tự nhận khách vào + tính tiền lịch của mình. */
  ktvTuThuTien: boolean
  /** Trần giảm giá khi thu tiền, basis point (1000 = 10%). */
  giamGiaToiDaBp: number
}
/** KH-GĐ2 GĐ3: màn "Ưu đãi" của chủ tiệm. */
export interface ManUuDai {
  mau: {
    kieu: DieuKhoanChuoi['kieu']
    giaTriBp: number
    giaTriVnd: null
    toiDaVnd: string
    dichVu: string[]
    denNgay: string
    tongSuat: number
  }
  chuongTrinh: {
    id: string
    moTa: string
    dieuKhoan: DieuKhoanChuoi
    tuNgay: string
    denNgay: string
    tongSuat: number
    daPhat: number
    dangBat: boolean
    conNhan: boolean
  } | null
  soLieu: {
    daNhan: number
    daDung: number
    tongThuVnd: string
    tongGiamVnd: string
    quayLai: number
  } | null
}
export interface LuuUuDai {
  id?: string
  kieu?: DieuKhoanChuoi['kieu']
  giaTriBp?: number | null
  giaTriVnd?: string | null
  toiDaVnd?: string | null
  dichVu?: string[]
  denNgay?: string
  tongSuat?: number
  dangBat?: boolean
}
export const uuDaiApi = {
  doc: () => goi<ManUuDai>('/v1/admin/uu-dai'),
  luu: (than: LuuUuDai) =>
    goi<ManUuDai>('/v1/admin/uu-dai', { method: 'POST', body: JSON.stringify(than) }),
}

export const congTacApi = {
  doc: () => goi<{ congTac: QuyenTho; coMaDuyet?: boolean }>('/v1/admin/cong-tac'),
  /** KH-GĐ2 6.2.4: chủ tiệm đặt / đổi mã duyệt giảm giá (khác mã đăng nhập). */
  datMaDuyet: (ma: string) =>
    goi<{ coMaDuyet: boolean }>('/v1/admin/ma-duyet', {
      method: 'POST',
      body: JSON.stringify({ ma }),
    }),
  luu: (sua: Partial<QuyenTho>) =>
    goi<{ congTac: QuyenTho }>('/v1/admin/cong-tac', { method: 'POST', body: JSON.stringify(sua) }),
}

export const caiDatApi = {
  doc: () => goi<CaiDatTiem>('/v1/admin/cai-dat'),
  luu: (sua: { tinhNang?: Partial<BoTinhNang> } & Partial<CaiDatTiem['datLich']>) =>
    goi<CaiDatTiem>('/v1/admin/cai-dat', { method: 'POST', body: JSON.stringify(sua) }),
}

/** Cấu hình → Thông tin tiệm: chủ tiệm đổi tên + ngành (docs/plans/thong-tin-tiem.md). Đọc thì lấy từ `ToiLaAi`. */
export const thongTinTiemApi = {
  luu: (sua: { ten: string; nganh: string }) =>
    goi<{ tenTiem: string; nganh: string; maTiem: string }>('/v1/admin/thong-tin-tiem', {
      method: 'POST',
      body: JSON.stringify(sua),
    }),
}

/**
 * Cấu hình → Thông tin tiệm → thẻ "Ảnh bìa và logo" (v0.23.3, ADR-124). Cả hai đều POST (không PUT / DELETE: CORS của API chỉ cho GET, POST):
 * tải = `POST …/anh-cua-hang/:loai`, gỡ = `POST …/anh-cua-hang/:loai/go`. Đọc ảnh đang dùng từ `ToiLaAi.anhCuaHang`.
 */
export const anhCuaHangApi = {
  tai: (loai: 'bia' | 'logo', dataBase64: string) =>
    goi<{ loai: string; duongDan: string; rong: number; cao: number; bytes: number }>(
      `/v1/admin/anh-cua-hang/${loai}`,
      { method: 'POST', body: JSON.stringify({ dataBase64 }) },
      GIOI_HAN_TAI_LEN_MS,
    ),
  go: (loai: 'bia' | 'logo') =>
    goi<{ loai: string; duongDan: null }>(`/v1/admin/anh-cua-hang/${loai}/go`, {
      method: 'POST',
      body: '{}',
    }),
}

/** Mã QR đặt lịch (ADR-056) + tình trạng dùng của tiệm (ADR-057). */
export interface MaDatLich {
  ma: string | null
  tinhTrang: 'mo' | 'tam-ngung' | 'het-dung-thu' | 'het-han'
  dungThuDen: string | null
  daTraDen: string | null
}
export const tiem = {
  /** Thẻ "Chuẩn bị tiệm" (1.3): máy chủ kiểm đúng điều kiện nhận lịch, client không tự suy. */
  chuanBi: () => goi<TinhTrangChuanBi>('/v1/admin/chuan-bi-tiem'),
  maDatLich: () => goi<MaDatLich>('/v1/admin/ma-dat-lich'),
  doiMaDatLich: () =>
    goi<{ ma: string }>('/v1/admin/ma-dat-lich/doi', { method: 'POST', body: '{}' }),
}

export interface Branch {
  id: string
  name: string
}
export interface ServiceOption {
  id: string
  label: string
  /** Chuỗi số nguyên đồng (ADR-026). */
  priceVnd: string
  durationMin: number
}
export interface Shift {
  startMin: number
  endMin: number
  source: 'auto' | 'manual'
}
export interface StaffColumn {
  staffId: string
  fullName: string
  color: string | null
  shifts: Shift[]
}
export interface DayAppointment {
  appointmentId: string
  itemId: string
  code: string
  status: string
  /** `miniapp` · `counter` … — khối lịch gắn nhãn "Zalo" cho khách tự đặt. Danh sách chưa gửi. */
  source?: string
  customerId?: string | null
  staffId: string | null
  startMin: number
  endMin: number
  customerName: string | null
  customerPhone: string | null
  serviceName: string
  /** Chuỗi số nguyên đồng (ADR-026); null = vai không xem được doanh thu và lịch không phải của hôm nay. */
  priceVnd: string | null
  serviceVariantId: string
  /** Dời được (kéo-thả / "Đổi giờ"): lịch chưa tới và chỉ một dịch vụ. */
  doiDuoc: boolean
  /** Pha C: đã có hoá đơn còn hiệu lực. Lịch "xong" mà chưa thu → quầy thấy nút "Thu tiền". */
  daThu?: boolean
  /** ADR-078: lịch có dòng của thợ khác — thợ tự thu không tính tiền lịch này (quầy thu). */
  nhieuTho?: boolean
  /** T3-N1 (0098): lịch đã từng "Đang làm" — không còn nút "Chưa tới" (lùi về "Đã đặt"). */
  daTungLam?: boolean
  /** N1: khách đã xác nhận "vẫn đến" / đã nhắc mà tới mốc chưa trả lời. */
  daXacNhan?: boolean
  chuaXacNhan?: boolean
  /** Đã từng đổi giờ: số lần + giờ trước lần đổi gần nhất ("09:00 28/09"). */
  soLanDoiGio?: number
  doiGioTu?: string | null
  /** Lời khách nhắn tiệm từ Mini App (0035), cũ trước. */
  loiNhan?: string[]
  /** N2: cọc đang giữ (chuỗi đồng). */
  cocVnd?: string
  /** Số cọc chốt lúc đặt (0041); null = lịch cũ, dùng số cấu hình. */
  cocCanVnd?: string | null
}
/** N2: cài đặt cọc của tiệm (chủ tiệm sửa). */
export interface CauHinhCoc {
  bat: boolean
  apDung: 'moi_lich' | 'khach_moi' | 'khach_hay_vang'
  soTienVnd: string | null
  giuPhut: number
  chinhSach: string | null
  taiKhoan: { bin: string; soTaiKhoan: string; tenTaiKhoan: string } | null
  nganHang: { bin: string; ten: string }[]
}
export interface DayBoard {
  date: string
  /** Tiệm đã từng có lịch nào chưa (4.2.3) — chưa thì mới hiện khung "Chưa có lịch nào". */
  tiemCoLich?: boolean
  /** N2: số tiền cọc tiệm cài (chuỗi đồng) — null khi chưa cài. */
  cocMacDinhVnd?: string | null
  openWindows: { startMin: number; endMin: number }[]
  /** K2 (ADR-111): chi nhánh đóng cửa cả ngày này. `lyDo` luôn `null` với thợ. Thiếu = không đóng (máy chủ cũ). */
  dongCua?: { lyDo: string | null } | null
  staff: StaffColumn[]
  appointments: DayAppointment[]
  /** STT 3.4: người nghỉ trong ngày này (kèm lịch cần xử lý). `[]` với thợ; thiếu ở máy chủ cũ. */
  nghi?: NghiTrongNgay[]
  /** BK-2 (ADR-096): người nghỉ việc / lịch ngoài ca hoặc ngoài giờ mở cửa. Thiếu = rỗng (máy chủ cũ). */
  ngoaiCa?: NgoaiCaTrongNgay[]
  /**
   * K9 (v0.22.2): lịch 7 ngày TRƯỚC mà khách đã tới nhưng quầy chưa đóng ("Đã tới" / "Đang làm" / "Xong" chưa thu), tối đa 20, cũ nhất
   * trước. Chỉ khi xem HÔM NAY và vai tính được tiền lịch; `[]` còn lại. Thiếu = rỗng (máy chủ cũ).
   */
  chuaDongTruoc?: LichChuaDongTruoc[]
}
/** Một lịch ngày trước còn dở (K9). `ngay` = `YYYY-MM-DD` của chính lịch đó. */
export interface LichChuaDongTruoc {
  appointmentId: string
  code: string
  ngay: string
  startMin: number
  trangThai: string
}
/** Một dòng của danh sách lịch hẹn — đủ trường để mở lại đúng tờ chi tiết của màn lịch. */
export interface LichHenDong {
  appointmentId: string
  customerId: string | null
  itemId: string | null
  code: string
  status: string
  source: string
  date: string
  startMin: number
  endMin: number
  customerName: string | null
  customerPhone: string | null
  serviceName: string
  serviceVariantId: string | null
  staffId: string | null
  staffName: string | null
  priceVnd: string | null
  doiDuoc: boolean
  /** Pha C: lịch đã có hoá đơn còn hiệu lực. */
  daThu?: boolean
  /** N2: cọc đang giữ (chuỗi đồng). */
  cocVnd?: string
  /** Số cọc chốt lúc đặt (0041); null = lịch cũ, dùng số cấu hình. */
  cocCanVnd?: string | null
  daXacNhan?: boolean
  chuaXacNhan?: boolean
  soLanDoiGio?: number
  doiGioTu?: string | null
}

export interface DanhSachLichHen {
  lichHen: LichHenDong[]
  theoTrangThai: Record<string, number>
  conNua: boolean
  trang: number
}

export interface Slot {
  startAt: string
  startMin: number
  staffChoiceCount: number
  /** KTV một mình làm được cả chuỗi lúc này — để lễ tân chọn người khi có từ 2 người rảnh. */
  staffChoiceIds: string[]
}
/** Tên gọi của KTV trong danh sách chọn người (phản hồi khe giờ). */
export interface KtvChon {
  id: string
  ten: string
}
export interface CustomerHit {
  id: string
  fullName: string
  phone: string | null
  noShowCount: number
  canhBao: string | null
  lastVisitAt?: string | null
  /** Lúc tạo lịch còn hiệu lực gần nhất (khách vừa đặt mà chưa tới); máy chủ cũ không trả. */
  datLichLuc?: string | null
  /** KH-GĐ2 4.3: lịch đã xong gần nhất — tờ đặt lịch điền sẵn dịch vụ + người làm. */
  lanTruoc?: {
    ngay: string
    variantIds: string[]
    staffId: string | null
    staffTen: string | null
  } | null
}

/**
 * `tenantId` chỉ còn dùng cho các đường dẫn **công khai** (`/v1/public/*`), vì khách chưa đăng
 * nhập được nên tiệm vẫn phải đi theo yêu cầu (ADR-010). Đường dẫn nội bộ **không** truyền tiệm
 * nữa: máy chủ lấy từ phiên, và cổng `pnpm check:auth` chặn việc truyền lại.
 */
export function createApi(tenantId: string) {
  const t = encodeURIComponent(tenantId)
  return {
    branches: () => goi<{ branches: Branch[] }>('/v1/admin/branches'),
    services: () => goi<{ services: ServiceOption[] }>('/v1/admin/services'),
    day: (branchId: string, date: string) =>
      goi<DayBoard>(`/v1/admin/day?branch=${branchId}&date=${date}`),
    // POST chu khong phai GET: so dien thoai tren query string se nam trong nhat ky may chu
    // va nhat ky cua reverse proxy - tuc la danh ba khach nam trong file log.
    /** Tên (không dấu), 3–4 số cuối hoặc số đủ; chuỗi rỗng = 10 khách vừa tới (KH-GĐ2 4.3). */
    findCustomer: (q: string) =>
      goi<{ customers: CustomerHit[] }>('/v1/admin/customers/tim', {
        method: 'POST',
        body: JSON.stringify({ q }),
      }),
    /** `staffId`: chỉ khe của đúng kỹ thuật viên đó (tạo lịch từ ô trống của một cột, "Đổi giờ"). */
    /** `variantIds`: một hoặc nhiều dịch vụ, làm liền nhau theo đúng thứ tự (tối đa 5). */
    slots: (
      branchId: string,
      date: string,
      variantIds: string | readonly string[],
      staffId?: string,
      boQuaLich?: string,
    ) =>
      goi<{ slots: Slot[]; ktv?: KtvChon[]; reason?: string; message?: string }>(
        `/v1/public/slots?tenant=${t}&branch=${branchId}&date=${date}&variants=${typeof variantIds === 'string' ? variantIds : variantIds.join(',')}` +
          (staffId === undefined ? '' : `&staff=${staffId}`) +
          (boQuaLich === undefined ? '' : `&boQua=${boQuaLich}`),
      ),
    book: (
      input: {
        branchId: string
        date: string
        startMin: number
        variantIds: readonly string[]
        fullName: string
        phone?: string
        staffId?: string
      },
      khoa: string,
    ) =>
      goi<{ appointmentId: string; code: string }>('/v1/public/appointments', {
        method: 'POST',
        // Khoá do màn đặt giữ THEO LỰA CHỌN (`useKhoaIdem`): mạng rớt sau khi máy chủ đã ghi → bấm lại gửi đúng
        // khoá cũ → máy chủ trả lại lịch cũ, không tạo lịch thứ hai (RETRO 24/09, lỗi lặp của Mini App).
        headers: { 'idempotency-key': khoa },
        body: JSON.stringify({
          tenant: tenantId,
          branch: input.branchId,
          date: input.date,
          startMin: input.startMin,
          variants: input.variantIds,
          ...(input.staffId ? { staffId: input.staffId } : {}),
          customer: {
            fullName: input.fullName,
            ...(input.phone ? { phone: input.phone } : {}),
          },
        }),
      }),
    /**
     * Dời lịch sang giờ / ngày / KTV khác. Máy chủ giữ đủ 4 lớp chống trùng; khoá chống lặp sinh
     * mới cho MỖI lần thả (thả hai lần vì mạng chậm không thành hai lần dời).
     */
    doiGio: (
      appointmentId: string,
      than: {
        date: string
        startMin: number
        staffId: string
        tuStaffId?: string
        /** Ngày + giờ GỐC người bấm nhìn thấy: lịch đã dời chỗ khác thì máy chủ trả 409 `LICH_VUA_DOI`. */
        tuDate?: string
        tuStartMin?: number
      },
      /**
       * Khoá chống lặp THEO Ý ĐỊNH (STT 3.4): màn "lịch cần xử lý" giữ một khoá cho `(lịch, người mới)` để bấm lại
       * sau khi mạng chập chờn không thành hai lần chuyển. Thiếu → sinh mới như cũ (mỗi lần thả một khoá).
       */
      khoa?: string,
    ) =>
      goi<{
        startMin: number
        endMin: number
        date: string
        staffId: string
        /** K1a: khách được báo bằng cách nào (chỉ khi đổi giờ thật của lịch có khách). */
        baoKhach?: BaoKhach
      }>(`/v1/admin/appointments/${appointmentId}/doi-gio`, {
        method: 'POST',
        headers: { 'idempotency-key': khoa ?? crypto.randomUUID() },
        body: JSON.stringify(than),
      }),
    /**
     * STT 3.4: cho một người nghỉ nguyên ngày HÔM NAY (máy chủ tính "hôm nay"). Quyền `xep-ca`. Luôn lưu — kể cả
     * khi còn lịch vướng — rồi trả danh sách lịch vướng để quầy chuyển người.
     */
    choNghiHomNay: (staffId: string) =>
      goi<{ id: string; lichVuong: LichVuongNghi[] }>(`/v1/admin/nhan-vien/${staffId}/ngay-nghi`, {
        method: 'POST',
        body: JSON.stringify({ homNay: true }),
      }),
    /** STT 3.4: lịch cần xử lý của một chi nhánh-ngày kèm người thay được. Quyền `dat-lich`; tối đa 30 dòng. */
    lichVuong: (branchId: string, date: string) =>
      goi<{ date: string; lich: DongLichVuong[] }>(
        `/v1/admin/lich-vuong?branch=${branchId}&date=${date}`,
      ),
    lichHen: (loc: {
      branchId: string
      tu: string
      den: string
      trangThai?: string[]
      ktv?: string
      q?: string
      trang?: number
    }) => {
      // POST: ô tìm có thể là số điện thoại, mà Caddy ghi cả query string vào nhật ký.
      return goi<DanhSachLichHen>('/v1/admin/lich-hen', {
        method: 'POST',
        body: JSON.stringify({
          branch: loc.branchId,
          tu: loc.tu,
          den: loc.den,
          ...(loc.trangThai !== undefined && loc.trangThai.length > 0
            ? { trangThai: loc.trangThai.join(',') }
            : {}),
          ...(loc.ktv !== undefined && loc.ktv !== '' ? { ktv: loc.ktv } : {}),
          ...(loc.q !== undefined && loc.q.trim() !== '' ? { q: loc.q.trim() } : {}),
          ...(loc.trang !== undefined ? { trang: loc.trang } : {}),
        }),
      })
    },
    /** Huỷ (`cancelled`) bắt buộc có lý do — khách đọc câu này trong "Của tôi" (N1, 0032). */
    setStatus: (appointmentId: string, to: string, reason?: string, tu?: string) =>
      goi<{ ok: boolean; status: string; baoKhach?: BaoKhach }>(
        `/v1/admin/appointments/${appointmentId}/status`,
        {
          method: 'POST',
          // `tu`: trạng thái người bấm đang thấy — máy chủ trả 409 `WRONG_STATE` nếu máy khác đã đổi (vòng sửa 3.1).
          body: JSON.stringify({
            to,
            ...(reason === undefined ? {} : { reason }),
            ...(tu === undefined ? {} : { tu }),
          }),
        },
      ),
    /** Lịch khách tự đặt khi tiệm bật duyệt tay: nhận, hoặc từ chối kèm lý do. */
    duyet: (appointmentId: string, nhan: boolean, lyDo?: string) =>
      goi<{ status: string; baoKhach?: BaoKhach }>(
        `/v1/admin/appointments/${appointmentId}/duyet`,
        {
          method: 'POST',
          headers: { 'idempotency-key': crypto.randomUUID() },
          body: JSON.stringify(lyDo === undefined ? { nhan } : { nhan, lyDo }),
        },
      ),
    /** N2: tiền cọc — mọi POST mang khoá do tờ giữ (`useKhoaIdem`). */
    coc: {
      nhan: (id: string, than: { soTienVnd: string; cach: 'transfer' | 'cash' }, khoa: string) =>
        gui<{ status: string; cocDangGiuVnd: string }>(
          `/v1/admin/appointments/${id}/nhan-coc`,
          than,
          khoa,
        ),
      /** `duKienVnd`: số cọc quầy đang thấy — sổ đổi trong lúc đó thì 409 (RETRO money M2). */
      hoan: (
        id: string,
        than: { cach: 'transfer' | 'cash'; lyDo: string; duKienVnd: string },
        khoa: string,
      ) => gui<{ daHoanVnd: string }>(`/v1/admin/appointments/${id}/hoan-coc`, than, khoa),
      giu: (id: string, lyDo: string, khoa: string) =>
        gui<{ hoaDon: { number: string; totalVnd: string } }>(
          `/v1/admin/appointments/${id}/giu-coc`,
          { lyDo },
          khoa,
        ),
      /** Sửa cọc ghi nhầm: đảo lần ghi nhận gần nhất (soát N2). */
      dao: (id: string, lyDo: string, khoa: string) =>
        gui<{ daDaoVnd: string; cocDangGiuVnd: string }>(
          `/v1/admin/appointments/${id}/dao-coc`,
          { lyDo },
          khoa,
        ),
      cauHinh: () => goi<CauHinhCoc>('/v1/admin/cai-dat/coc'),
      /** `maXacNhan`: bắt buộc khi ĐỔI tài khoản nhận tiền (soát N2). */
      luu: (than: Omit<CauHinhCoc, 'nganHang'> & { maXacNhan?: string }, khoa: string) =>
        gui<{ ok: boolean }>('/v1/admin/cai-dat/coc', than, khoa),
    },
    /** N4: khách vãng lai — gợi ý kỹ thuật viên theo lượt xoay vòng, rồi tạo lịch "Đã tới". */
    khachVao: {
      goiY: (branchId: string, variantIds: string[]) =>
        goi<{
          startMin: number | null
          batDau: string | null
          goiY: string | null
          ungVien: { staffId: string; ten: string; luot: number }[]
        }>(`/v1/admin/khach-vao/goi-y?branch=${branchId}&variants=${variantIds.join(',')}`),
      /** Khoá do tờ giữ (`useKhoaIdem`): rớt mạng rồi bấm lại KHÔNG thành hai lịch (soát N4 BLOCKER). */
      tao: (
        than: {
          branchId: string
          variantIds: string[]
          staffId: string
          goiYDaThay: string | null
          tenKhach?: string
          sdt?: string
        },
        khoa: string,
      ) =>
        gui<{ appointmentId: string; code: string; staffId: string }>(
          '/v1/admin/khach-vao',
          than,
          khoa,
        ),
    },
    /** Khách trả lời "vẫn đến" qua chat Zalo → quầy ghi thay. */
    khachXacNhan: (appointmentId: string) =>
      goi<{ ok: boolean }>(`/v1/admin/appointments/${appointmentId}/khach-xac-nhan`, {
        method: 'POST',
        body: '{}',
      }),
    canNhac: (branchId: string, ngay: string) =>
      goi<{ ngay: string; lich: DongCanNhac[] }>(
        `/v1/admin/can-nhac?ngay=${ngay}&branchId=${branchId}`,
      ),
    ghiDaNhac: (appointmentId: string) =>
      goi<{ ok: boolean }>('/v1/admin/lien-he', {
        method: 'POST',
        body: JSON.stringify({ appointmentId, loai: 'nhac_lich', kenh: 'zalo_tay' }),
      }),
    zaloChoXacNhan: () => goi<{ khach: ZaloChoXacNhan[] }>('/v1/admin/khach/zalo-cho-xac-nhan'),
    xacNhanZalo: (customerId: string, dungNguoi: boolean) =>
      goi<{ ok: boolean }>(`/v1/admin/khach/${customerId}/zalo`, {
        method: 'POST',
        body: JSON.stringify({ dungNguoi }),
      }),
    ...goiViApi,
    ...thuTienApi,
    ...khoApi,
    ...chamKhachApi,
    ...thongBaoApi,
  }
}

// ------------------------------------------------------------------ Gói liệu trình + ví (B5)
/** Số dư ví. Tiền là chuỗi số nguyên đồng (ADR-026), kèm `ledgerSeq` để không hiện số cũ. */
export interface ViKhach {
  cashVnd: string
  bonusVnd: string
  ledgerSeq: string
}
export interface GoiKhach {
  id: string
  name: string
  dichVu: string
  serviceVariantId: string
  sessionsLeft: number
  sessionsTotal: number
  sessionsUsed: number
  pricePaidVnd: string
  expiresAt: string
  trangThai: 'con' | 'het-han' | 'het-buoi' | 'da-dong'
  /** Gói nhập từ sổ tay cũ — chủ tiệm huỷ được nếu nhập nhầm và chưa dùng buổi nào. */
  soCu: boolean
  /** Gói TẶNG nhập từ sổ cũ (STT 2.3): mọi buổi 0đ, thợ không có hoa hồng. Máy chủ cũ chưa gửi thì vắng. */
  tang?: boolean
  ledgerSeq: string
}
export interface GoiViCuaKhach {
  khach: {
    ten: string
    ngaySinh: string | null
    /** `+84…` (quầy) hoặc đã che `0912***678`; `null` = chưa có số. Máy chủ cũ không trả. */
    sdt?: string | null
    /** "Lưu ý cố định" (ADR-089). `null` = chưa có. Máy chủ cũ không trả. */
    luuY?: string | null
    /** Hồ sơ gắn Zalo theo số, quầy chưa xác nhận đúng người — chưa đổi số được. */
    choXacNhanZalo?: boolean
    /** Hồ sơ đã ẩn danh (khách xin xoá dữ liệu) — không sửa được. */
    daXoa?: boolean
  }
  vi: ViKhach
  goi: GoiKhach[]
  diem?: { diem: string; ledgerSeq: string } | null
}
export interface GoiMau {
  id: string
  serviceVariantId: string
  dichVu: string
  name: string
  paidSessions: number
  bonusSessions: number
  priceVnd: string
  validityDays: number
  isActive: boolean
  giaLeVnd: string
}
export interface MucTangVi {
  id: string
  minTopupVnd: string
  bonusBp: number
}
export interface GoiPhuHop {
  coKhach: boolean
  /** "Lưu ý cố định" của khách (đã che số theo vai); `null` khi không khách / chờ xác nhận Zalo / chưa có. */
  luuY?: string | null
  status: string
  ngay?: string
  dichVu: {
    itemId: string
    /** KH-GĐ2 GĐ3: để tính giá sau ưu đãi (dịch vụ áp dụng) bằng hàm dùng chung. */
    serviceVariantId?: string | null
    dichVu: string
    priceVnd: string
    daTruGoi: string | null
    goi: GoiKhach[]
  }[]
  /** KH-GĐ2 GĐ3: ưu đãi khách mới còn dùng được (null khi tắt / không có). */
  uuDai?: UuDaiKhach | null
  /** C6: chỉ có ở tờ thu của THỢ — khách có ưu đãi chưa dùng (không kèm id / điều khoản); thợ nhờ quầy tính tiền. */
  coUuDai?: boolean
  vi: ViKhach | null
  /** Pha E: điểm khách có + giá trị 1 điểm; null khi tiệm chưa bật điểm thưởng. */
  diem?: { co: string; ledgerSeq: string; motDiemVnd: string } | null
  daTruViVnd: string
  /** N2: cọc khách đã chuyển cho lịch này, đang giữ — trừ TRƯỚC ví (0037). */
  cocGiuVnd?: string
  conPhaiTraVnd: string
  /** Tiệm bật hoa hồng VÀ tính trên giá gốc (ADR-076) — tờ thu tiền nhắc khi giảm hơn nửa. */
  hoaHongTruocGiam?: boolean
  /** KH-GĐ2 5.2: thợ làm lịch — chọn người nhận bo. */
  tho?: { id: string; ten: string }[]
}
/** Điều khoản ưu đãi dạng chuỗi (tiền là chuỗi số nguyên đồng, ADR-026). */
export interface DieuKhoanChuoi {
  kieu: 'phan_tram' | 'so_tien' | 'tang_dich_vu'
  giaTriBp: number | null
  giaTriVnd: string | null
  toiDaVnd: string | null
  dichVu: string[]
}
export interface UuDaiKhach {
  id: string
  moTa: string
  hetHan: string
  dieuKhoan: DieuKhoanChuoi
}
export function dieuKhoanTuChuoi(d: DieuKhoanChuoi) {
  return {
    kieu: d.kieu,
    giaTriBp: d.giaTriBp,
    giaTriVnd: d.giaTriVnd === null ? null : BigInt(d.giaTriVnd),
    toiDaVnd: d.toiDaVnd === null ? null : BigInt(d.toiDaVnd),
    dichVu: d.dichVu,
  }
}
export interface DongSoCai {
  id: string
  seq: string
  kind: string
  sessionsDelta?: number
  valueVnd?: string
  refundVnd?: string
  cashDeltaVnd?: string
  bonusDeltaVnd?: string
  note: string | null
  luc: string
  nguoi: string | null
  daDao: boolean
}
export interface BaoCaoGoiVi {
  tu: string
  den: string
  tienDaThu: { tongVnd: string; banGoiVnd: string; napViVnd: string; daHoanVnd: string }
  doanhThuThucHien: { tongVnd: string; tuGoiVnd: string; tuViVnd: string }
  conNoKhach: {
    tongVnd: string
    soBuoi: number
    tienGoiVnd: string
    viThatVnd: string
    viTangVnd: string
    goiHetHanVnd: string
  }
}
export type HinhThucThu = 'cash' | 'transfer' | 'card'

/**
 * POST đụng tiền, kèm khoá chống lặp do BÊN GỌI giữ (`useKhoaIdem`): một khoá cho cả lần mở tờ.
 * Rớt mạng sau khi máy chủ đã ghi → bấm lại gửi ĐÚNG khoá cũ → máy chủ trả lại phản hồi cũ, không thu
 * tiền lần hai (security-privacy-reviewer B5, NẶNG 1). Sinh khoá mới cho mỗi lần bấm là sai.
 */
function gui<T>(path: string, than: unknown, khoa: string): Promise<T> {
  return goi<T>(path, {
    method: 'POST',
    headers: { 'idempotency-key': khoa },
    body: JSON.stringify(than),
  })
}

/** Hoá đơn vừa sinh lúc hoàn tất lịch. */
export interface HoaDonMoi {
  id: string
  number: string
  subtotalVnd: string
  discountVnd: string
  totalVnd: string
  walletVnd: string
  conLaiVnd: string
  payMethod: string
  /** KH-GĐ2 5.2: bo đã ghi cho từng thợ (ngoài hoá đơn). */
  tienBo?: { staffId: string; soTienVnd: string }[]
}

/** KH-GĐ2 5.2: bo gửi kèm lúc thu tiền — máy chủ chia đều nếu không có `phan`. */
export interface TienBoGui {
  soTienVnd: string
  hinhThuc: HinhThucThu
  thoIds: string[]
  phan?: { staffId: string; soTienVnd: string }[]
}

/** KH-GĐ2 5.2: Sổ tiền → bo từng thợ trong ngày + tiệm còn nợ. */
export interface ThoBo {
  staffId: string
  ten: string
  tienMatVnd: string
  chuyenKhoanVnd: string
  theVnd: string
  dangNoVnd: string
  /** Các lần "trả thợ" trong ngày — sửa lần bấm nhầm. */
  traHomNay: { id: string; soTienVnd: string; daDao: boolean }[]
}

/** KH-GĐ2 5.3: một khoản chi trong tháng. */
export interface KhoanChi {
  id: string
  /** Dòng bỏ một khoản ghi nhầm (số âm), nằm ở tháng nó xảy ra. */
  laDao: boolean
  /** Ngày của khoản gốc ("31/10") khi là dòng bỏ. */
  ngayGoc: string | null
  nhom: string | null
  tenNhom: string | null
  soTienVnd: string
  cach: 'cash' | 'transfer'
  note: string | null
  luc: string
  nguoi: string | null
  daDao: boolean
}

/** KH-GĐ2 5.3: tiền lời một tháng theo dòng tiền. */
export interface TienLoi {
  thang: string
  thuVnd: string
  /** Tiền trả lại khách khi hoàn gói / hoàn ví trong tháng. */
  traLaiVnd: string
  chiVnd: string
  loiVnd: string
  traTruocVnd: string
  khoanChi: KhoanChi[]
}

/** KH-GĐ2 7.2.5: một hoá đơn bị huỷ (tiền là chuỗi đồng, giờ ISO +07:00). */
export interface HoaDonHuy {
  id: string
  so: string
  maLich: string | null
  khach: string | null
  /** Tên chi nhánh của hoá đơn (v0.24.3). Tuỳ chọn: máy chủ cũ không trả. */
  chiNhanh?: string | null
  tienTruocVnd: string
  lucThu: string
  nguoiThu: string | null
  lucHuy: string
  nguoiHuy: string | null
  lyDo: string | null
  thuLai: { so: string; tienVnd: string; conHieuLuc: boolean } | null
}
export interface HoaDon {
  id: string
  number: string
  status: 'paid' | 'void'
  appointmentId: string | null
  subtotalVnd: string
  discountVnd: string
  discountReason: string | null
  totalVnd: string
  payMethod: string
  paidAt: string
  voidReason: string | null
  khach: string | null
  thuNgan: string | null
  /** In hoá đơn giấy: chi nhánh thu tiền + số chủ tiệm (E.164, có thể null). */
  inThongTin: {
    chiNhanh: string | null
    diaChi: string | null
    sdtChiNhanh: string | null
    sdtChuTiem: string | null
  }
  dong: {
    label: string
    priceVnd: string
    discountVnd: string
    netVnd: string
    paidBy: string
    ktv: string | null
  }[]
  traBang: { method: string; soVnd: string }[]
}

export interface Ket {
  tu: string
  tuLanChotTruoc: boolean
  canCoVnd: string
  /** 0066: người chốt lần trước (người để lại tiền đầu kỳ). */
  dauKyBoi?: string | null
  chiTiet: {
    /** KH-GĐ2 7.3.3 (0066): tiền lần chốt trước để lại két. */
    dauKyVnd?: string
    /** Cọc tiền mặt ròng (nhận − hoàn), có thể âm. */
    nhanCocVnd?: string
    thuDichVuVnd: string
    banGoiVnd: string
    napViVnd: string
    traHoanViVnd: string
    traHoanGoiVnd: string
    /** KH-GĐ2 5.2: tiền bo trả thợ bằng tiền mặt. */
    traBoVnd?: string
    /** KH-GĐ2 5.3: khoản chi trả bằng tiền mặt. */
    chiTienMatVnd?: string
  }
  lichSu: {
    id: string
    luc: string
    canCoVnd: string
    demVnd: string
    lechVnd: string
    note: string | null
    nguoi: string | null
    /** 0066: tiền để lại két ở lần chốt này. */
    giuLaiVnd?: string
  }[]
}

export interface BangSo {
  ngay: string
  laHomNay: boolean
  tienVaoVnd: string
  thuDichVu: {
    tienMatVnd: string
    chuyenKhoanVnd: string
    theVnd: string
    viVnd: string
    cocVnd?: string
  }
  /** Cọc nhận ròng trong ngày (nhận − hoàn), có thể âm. */
  nhanCocVnd?: string
  /** Từng dòng cọc chuyển khoản trong ngày — chủ tiệm dò với sao kê (RETRO M4). */
  cocChuyenKhoan?: {
    luc: string
    maLich: string
    soTienVnd: string
    loai: string
    nguoi: string | null
  }[]
  /** KH-GĐ2 5.1: từng dòng chuyển khoản lúc thu trong ngày — chủ tiệm dò với sao kê. */
  thuChuyenKhoan?: {
    luc: string
    maLich: string | null
    soHoaDon: string
    soTienVnd: string
    nguoi: string | null
    /** Dòng tiền bo khách chuyển khoản / quẹt thẻ (không phải tiền hoá đơn). */
    bo?: boolean
    cach?: string
  }[]
  banGoiVnd: string
  banSanPhamVnd: string
  napViVnd: string
  giamGiaVnd: string
  soHoaDon: number
  soHuy: number
  lich: { tong: number; xong: number; khongDen: number; huy: number }
  hoaHongThang: { ten: string; tienVnd: string; soDichVu: number }[]
}

export interface BangLuong {
  thang: string
  daChot: {
    luc: string
    nguoi: string | null
    tongVnd: string
    /** KH-GĐ2 5.3: đã ghi thành khoản chi bao nhiêu (null = chưa). */
    daGhiChiVnd?: string | null
  } | null
  tongVnd: string
  dong: { staffId: string; ten: string; tienVnd: string; soDichVu: number }[]
}

export interface QuyTacHoaHong {
  /** ADR-076: hoa hồng tính trên giá trước giảm (true) hay sau giảm (false). Gửi thiếu = giữ nguyên. */
  truocGiam?: boolean
  macDinhBp: number
  rieng: { serviceVariantId: string; rateBp: number }[]
}

export interface ViecCuaToi {
  ngay: string
  viec: {
    appointmentId: string
    tu: string
    den: string
    dichVu: string
    khach: string | null
    status: string
  }[]
  hoaHongThangVnd: string
  /** Số khách đã làm xong (N4): hôm nay và tháng này. */
  soKhachHomNay: number
  soKhachThang: number
  hoaHong: { luc: string; dichVu: string; tienVnd: string; dao: boolean }[]
  /** KH-GĐ2 5.2.5: bo của chính mình — `null` khi tiệm tắt "Tiền bo". */
  tienBo?: { homNayVnd: string; thangVnd: string; dangNoVnd: string } | null
}

export interface SanPhamKho {
  id: string
  name: string
  unit: string
  sellPriceVnd: string | null
  minQty: number
  isActive: boolean
  ton: string
  ledgerSeq: string
  sapHet: boolean
  am: boolean
}

export interface DongSoKho {
  id: string
  seq: string
  kind: string
  qtyDelta: number
  unitCostVnd: string | null
  note: string | null
  luc: string
  nguoi: string | null
  soHoaDon: string | null
  daDao: boolean
}

export interface DinhMuc {
  serviceVariantId: string
  productId: string
  qty: number
}

export interface DongCho {
  id: string
  branchId: string
  serviceVariantId: string
  ngay: string
  dichVu: string
  chiNhanh: string
  khach: string
  phone: string | null
  customerId: string
  coCho: number[]
}

export interface DanhSachChamKhach {
  loai: 'sinh-nhat' | 'lau-chua-quay-lai' | 'goi-sap-het'
  khach: { id: string; ten: string; phone: string | null; nhanTin: boolean; moc: string | null }[]
  soNguoi: number
  soNhanTin: number
  uocTinhVnd: string
  tuDongGui: boolean
}

export interface QuyTacDiem {
  batDau: boolean
  earnPerVnd: string
  pointValueVnd: string
}

/** Một dòng trong chuông thông báo (GET /v1/admin/thong-bao). Không bao giờ có số điện thoại. */
export interface TinQuay {
  id: string
  loai: LoaiTinQuay
  luc: string
  lichId: string | null
  maLich: string | null
  batDau: string | null
  ngayCho: string | null
  branchId: string
  dichVu: string
  khach: string | null
  /** Lời khách nhắn (chỉ `khach_nhan`) — lời MỚI NHẤT của lịch đó. */
  noiDung: string | null
  /** Tổng số lời khách nhắn cho lịch này trong 3 ngày (chuông gộp theo lịch); 0 với tin khác. */
  soLoiNhan: number
  /** 0065: số hoá đơn bị huỷ; tiền (tổng hoá đơn / lệch két, chuỗi đồng); lý do; người làm. */
  soHoaDon?: string | null
  tienVnd?: string | null
  lyDo?: string | null
  nguoi?: string | null
}

/** Đăng ký Web Push của trình duyệt (PushSubscription.toJSON()). */
export interface DangKyDay {
  endpoint: string
  keys: { p256dh: string; auth: string }
}

const thongBaoApi = {
  thongBao: () => goi<{ tin: TinQuay[] }>('/v1/admin/thong-bao'),
  khoaDay: () => goi<{ khoa: string | null }>('/v1/admin/push/khoa'),
  trangThaiDay: (endpoint: string) =>
    goi<{ dangNhan: boolean }>('/v1/admin/push/trang-thai', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),
  dangKyDay: (dk: DangKyDay) =>
    goi<{ ok: boolean }>('/v1/admin/push/dang-ky', { method: 'POST', body: JSON.stringify(dk) }),
  huyDay: (endpoint: string) =>
    goi<{ ok: boolean }>('/v1/admin/push/huy', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),
}

/** N3: một khách đến hạn quay lại (docs/plans/pv2-n3-quay-lai.md). Ngày là 'YYYY-MM-DD' giờ VN. */
export interface DongDenHan {
  customerId: string
  ten: string
  sdt: string | null
  sdtZalo: string | null
  dichVu: string
  lanCuoi: string
  denHan: string
  /** Khách đồng ý nhận tin. Chưa thì không có lời mời / nút mở Zalo. */
  nhanTin: boolean
  loiMoi: string | null
}

/** N3: "Lần trước" của khách tính từ lịch đang mở. */
export interface LanTruoc {
  ghiChu: { noiDung: string; ngay: string; nguoi: string | null }[]
  anh: { id: string; kind: string }[]
  ngayLanTruoc: string | null
  /** Khách đã đồng ý lưu ảnh — hỏi TRƯỚC khi mở máy ảnh. */
  daDongYAnh: boolean
  /** Ảnh mới giữ bao nhiêu ngày (câu hỏi đồng ý nói đúng số này). */
  hanGiuNgay: number
  /** Kho ảnh khách của máy chủ ở nước ngoài (R2): câu hỏi đồng ý nói thêm "ngoài Việt Nam". */
  luuNgoaiVN?: boolean
  /** Hồ sơ đang chờ quầy xác nhận Zalo — lần trước để trống cho tới khi xác nhận đúng người. */
  choXacNhanZalo?: boolean
  /** "Lưu ý cố định" của khách (đã che số theo vai); `null` khi chờ xác nhận Zalo / chưa có. */
  luuY?: string | null
}

/** `POST /v1/admin/khach` (STT 2.1): 201 { id, ten, sdt, sdtDaDoi }. */
export interface KhachMoi {
  id: string
  ten: string
  sdt: string | null
  /** Số cũ 11 chữ số đã đổi đầu số. */
  sdtDaDoi: boolean
}

// ---- Gộp hồ sơ khách trùng (STT 2.5, ADR-091) — chữ lý do do MÁY CHỦ trả (messageVi), client không viết lại.

/** Một hồ sơ trong gợi ý trùng — cùng dạng `CustomerHit` của tìm khách, thêm `coZalo`. */
export interface UngVienTrung {
  id: string
  fullName: string
  phone: string | null
  lastVisitAt: string | null
  coZalo: boolean
}

export interface TomTatGop {
  id: string
  ten: string
  phone: string | null
  ngaySinh: string | null
  taoLuc: string
  soLich: number
  soLichSapToi: number
  lanCuoi: string | null
  coZalo: boolean
  choXacNhanZalo: boolean
  coTien: boolean
  luuY: string | null
  soGhiChu: number
  soAnh: number
  uuDaiChuaDung: number
}

/** Một cặp lịch sắp tới của hai hồ sơ chồng giờ (mã lịch hiện cho quầy, giờ là ISO). */
/** Một hồ sơ chờ quầy xác nhận Zalo (0034 / gộp). Máy chủ cũ không trả các trường sau `luc`. */
export interface ZaloChoXacNhan {
  customerId: string
  ten: string
  phone: string | null
  luc: string
  /** Chờ này sinh từ GỘP hồ sơ (ADR-091) — thẻ nói rõ nguồn. */
  tuGop?: boolean
  /** Số lịch hồ sơ này đặt qua Mini App + giờ lịch gần nhất — để quầy đối chiếu với khách. */
  soLichQuaZalo?: number
  lichQuaZaloLuc?: string | null
  /** `false` = hồ sơ chờ sinh từ gộp mà vai của người xem không được bấm "Đúng người" (chỉ chủ / quản lý). */
  duocXacNhan?: boolean
}

export interface CapLichTrung {
  codeA: string
  codeB: string
  tuLuc: string
  denLuc: string
}

export interface CanhBaoGop {
  ma: 'zalo-chuyen' | 'cho-xac-nhan' | 'zalo-giu' | 'uu-dai' | 'anh-se-xoa' | 'lich-trung'
  n?: number
  /** `zalo-giu`: số điện thoại của hồ sơ bị gộp sẽ gắn vào hồ sơ có Zalo. */
  soGan?: boolean
  /** `lich-trung`: các cặp lịch chồng giờ. */
  cap?: CapLichTrung[]
}

/** Kết quả của MỘT chiều gộp ("giữ a, gộp b" hoặc ngược lại). */
export interface XemTruocChieu {
  duocGop: boolean
  /** Câu lý do máy chủ trả khi không gộp được. */
  lyDo: string | null
  maLyDo: string | null
  /** Hai số khác nhau → phải chọn giữ số nào. */
  canChonSo: boolean
  ketQua: {
    ten: string
    phoneTheoSoGiu: { giu: string | null; bo: string | null }
    ngaySinh: string | null
    nhanTin: boolean
    luuAnh: boolean
    coZalo: boolean
    luuY: string | null
  } | null
  canhBao: CanhBaoGop[]
}

export interface XemTruocGop {
  a: TomTatGop
  b: TomTatGop
  goiY: 'a' | 'b' | null
  giuA: XemTruocChieu
  giuB: XemTruocChieu
  lyDoChung: string | null
}

export interface KetQuaGop {
  giuId: string
  boId: string
  soLich: number
  soGhiChu: number
  soAnh: number
  soCho: number
  soThongBao: number
  anhSeXoa: number
}

/** Kết quả xem trước / nhập danh sách khách (STT 2.2). Không có dữ liệu cá nhân — chỉ chỉ số dòng + mã. */
export interface KetQuaNhapKhach {
  thu: boolean
  soMoi: number
  soDaCo: number
  /** Trong `soDaCo`: hồ sơ có sẵn (khớp theo số) được ghi là "đã từng tới". */
  soDaCoCapNhat: number
  soLoi: number
  soChuY: number
  kieuNgay: { ngaySinh: 'dmy' | 'mdy'; lanCuoi: 'dmy' | 'mdy' }
  /** Cùng thứ tự, cùng độ dài với các dòng đã gửi. */
  dong: { kq: 'moi' | 'da-co' | 'loi'; ma: MaDongNhap[]; trungVoi?: number }[]
}

const chamKhachApi = {
  denHan: () => goi<{ khach: DongDenHan[] }>('/v1/admin/den-han'),
  /** "Đã liên hệ": ẩn khách khỏi danh sách đến hạn 14 ngày (sổ `contact_log`). */
  daLienHeQuayLai: (customerId: string, kenh: 'zalo_tay' | 'goi') =>
    goi<{ ok: boolean }>('/v1/admin/lien-he', {
      method: 'POST',
      body: JSON.stringify({ customerId, loai: 'moi_quay_lai', kenh, hoanNgay: 14 }),
    }),
  lanTruoc: (appointmentId: string) =>
    goi<LanTruoc>(`/v1/admin/appointments/${appointmentId}/lan-truoc`),
  ghiChuBuoi: (appointmentId: string, noiDung: string) =>
    goi<{ id: string }>(`/v1/admin/appointments/${appointmentId}/ghi-chu`, {
      method: 'POST',
      body: JSON.stringify({ noiDung }),
    }),
  /**
   * Đồng ý: kèm CÂU ĐÃ HIỆN (số ngày giữ + có nói "ngoài Việt Nam"); máy chủ so với cấu hình hiện tại, lệch → 409 `CAU_HOI_DOI`
   * (soát bảo mật R2 vòng 2 V3). Rút đồng ý không cần.
   */
  dongYAnh: (
    customerId: string,
    dongY: boolean,
    daHien?: { hanGiuNgay: number; luuNgoaiVN: boolean },
  ) =>
    goi<{ dongY: boolean; anhSeXoa: number }>(`/v1/admin/khach/${customerId}/dong-y-anh`, {
      method: 'POST',
      body: JSON.stringify(daHien === undefined ? { dongY } : { dongY, ...daHien }),
    }),
  taiAnh: (than: {
    customerId: string
    appointmentId: string
    kind: 'before' | 'after'
    dataBase64: string
  }) =>
    goi<{ id: string; purgeAfter: string }>(
      '/v1/admin/photos',
      { method: 'POST', body: JSON.stringify(than) },
      GIOI_HAN_TAI_LEN_MS,
    ),
  urlAnh: (photoId: string) =>
    goi<{ url: string; expiresInSec: number }>(`/v1/admin/photos/${photoId}/url`),
  choCho: (branchId: string) => goi<{ cho: DongCho[] }>(`/v1/admin/cho?branchId=${branchId}`),
  chamKhach: (loai: DanhSachChamKhach['loai'], soNgay: number) =>
    goi<DanhSachChamKhach>(`/v1/admin/cham-khach?loai=${loai}&soNgay=${soNgay}`),
  luuNgaySinh: (customerId: string, ngaySinh: string | null) =>
    goi<{ ok: boolean }>(`/v1/admin/khach/${customerId}/ngay-sinh`, {
      method: 'POST',
      body: JSON.stringify({ ngaySinh }),
    }),
  quyTacDiem: () => goi<QuyTacDiem>('/v1/admin/cau-hinh/diem'),
  luuQuyTacDiem: (than: QuyTacDiem) =>
    goi<QuyTacDiem>('/v1/admin/cau-hinh/diem', { method: 'POST', body: JSON.stringify(than) }),
}

const khoApi = {
  kho: (branchId: string | null) =>
    goi<{ sanPham: SanPhamKho[] }>(
      `/v1/admin/kho${branchId === null ? '' : `?branchId=${branchId}`}`,
    ),
  soKho: (branchId: string, productId: string) =>
    goi<{ dong: DongSoKho[] }>(`/v1/admin/kho/so?branchId=${branchId}&productId=${productId}`),
  nhapKho: (
    than: {
      branchId: string
      productId: string
      qty: number
      unitCostVnd: string | null
      note: string | null
    },
    khoa: string,
  ) => gui<{ tonMoi: { ton: string; ledgerSeq: string } }>('/v1/admin/kho/nhap', than, khoa),
  kiemKe: (
    than: { branchId: string; dem: { productId: string; qty: number }[]; note: string },
    khoa: string,
  ) =>
    gui<{ dieuChinh: { productId: string; lech: string }[] }>('/v1/admin/kho/kiem-ke', than, khoa),
  luuSanPham: (
    id: string | null,
    than: {
      name: string
      unit: string
      sellPriceVnd: string | null
      minQty: number
      isActive: boolean
    },
  ) =>
    goi<{ id: string }>(`/v1/admin/cau-hinh/san-pham${id === null ? '' : `/${id}`}`, {
      method: 'POST',
      body: JSON.stringify(than),
    }),
  dinhMuc: () => goi<{ dinhMuc: DinhMuc[] }>('/v1/admin/cau-hinh/dinh-muc'),
  luuDinhMuc: (than: { serviceVariantId: string; dong: { productId: string; qty: number }[] }) =>
    goi<{ dinhMuc: DinhMuc[] }>('/v1/admin/cau-hinh/dinh-muc', {
      method: 'POST',
      body: JSON.stringify(than),
    }),
  banLe: (
    than: {
      branchId: string
      sanPham: { productId: string; qty: number }[]
      hinhThuc: HinhThucThu
      giamVnd: string
      lyDoGiam: string | null
      duKienVnd: string
    },
    khoa: string,
  ) => gui<{ hoaDon: HoaDonMoi }>('/v1/admin/ban-le', than, khoa),
}

const thuTienApi = {
  hoaDonNgay: (ngay: string) => goi<{ hoaDon: HoaDon[] }>(`/v1/admin/hoa-don?ngay=${ngay}`),
  /** KH-GĐ2 7.2.5: hoá đơn bị huỷ trong ngày (theo giờ huỷ). */
  hoaDonHuy: (ngay: string) =>
    goi<{ ngay: string; hoaDon: HoaDonHuy[] }>(`/v1/admin/hoa-don-huy?ngay=${ngay}`),
  hoaDonCuaLich: (appointmentId: string) =>
    goi<{ hoaDon: HoaDon[] }>(`/v1/admin/appointments/${appointmentId}/hoa-don`),
  huyHoaDon: (invoiceId: string, lyDo: string, khoa: string) =>
    gui<{
      ok: boolean
      number: string
      boDaDao?: { ten: string; soTienVnd: string; hinhThuc: string }[]
    }>(`/v1/admin/hoa-don/${invoiceId}/huy`, { lyDo }, khoa),
  ket: (branchId: string) => goi<Ket>(`/v1/admin/ket?branchId=${branchId}`),
  chotKet: (
    than: { branchId: string; demVnd: string; giuLaiVnd: string; note: string | null },
    khoa: string,
  ) => gui<{ canCoVnd: string; demVnd: string; lechVnd: string }>('/v1/admin/ket/chot', than, khoa),
  bangSo: (ngay: string) => goi<BangSo>(`/v1/admin/bang-so?ngay=${ngay}`),
  bangLuong: (thang: string) => goi<BangLuong>(`/v1/admin/bang-luong?thang=${thang}`),
  chotBangLuong: (thang: string, khoa: string) =>
    gui<BangLuong>('/v1/admin/bang-luong/chot', { thang }, khoa),
  quyTacHoaHong: () => goi<QuyTacHoaHong>('/v1/admin/cau-hinh/hoa-hong'),
  luuQuyTacHoaHong: (than: QuyTacHoaHong) =>
    goi<QuyTacHoaHong>('/v1/admin/cau-hinh/hoa-hong', {
      method: 'POST',
      body: JSON.stringify(than),
    }),
  viecCuaToi: () => goi<ViecCuaToi>('/v1/admin/viec-cua-toi'),
  /** KH-GĐ2 5.2: bo từng thợ trong ngày + tiệm còn nợ. */
  tienBo: (ngay: string) => goi<{ thoBo: ThoBo[] }>(`/v1/admin/tien-bo?ngay=${ngay}`),
  /** KH-GĐ2 5.2: tiệm trả thợ phần bo đang nợ bằng tiền mặt từ két. */
  traBo: (than: { staffId: string; branchId: string; soTienVnd: string }, khoa: string) =>
    gui<{ id: string; conNoVnd: string }>('/v1/admin/tien-bo/tra', than, khoa),
  /** Sửa một lần "trả thợ" bấm nhầm. */
  daoTraBo: (tipId: string, lyDo: string, khoa: string) =>
    gui<{ id: string }>(`/v1/admin/tien-bo/tra/${tipId}/dao`, { lyDo }, khoa),
  /** KH-GĐ2 5.3: khoản chi và tiền lời. */
  nhomChi: () => goi<{ nhom: { id: string; ten: string }[] }>('/v1/admin/khoan-chi/nhom'),
  themNhomChi: (ten: string, khoa: string) =>
    gui<{ id: string; ten: string }>('/v1/admin/khoan-chi/nhom', { ten }, khoa),
  ghiKhoanChi: (
    than: {
      nhom: string | null
      groupId: string | null
      soTienVnd: string
      hinhThuc: 'cash' | 'transfer'
      branchId: string
      note: string | null
    },
    khoa: string,
  ) => gui<{ id: string }>('/v1/admin/khoan-chi', than, khoa),
  daoKhoanChi: (id: string, lyDo: string, khoa: string) =>
    gui<{ id: string }>(`/v1/admin/khoan-chi/${id}/dao`, { lyDo }, khoa),
  ghiHoaHongThang: (
    than: { thang: string; branchId: string; hinhThuc: 'cash' | 'transfer'; soTienVnd: string },
    khoa: string,
  ) => gui<{ id: string; soTienVnd: string }>('/v1/admin/khoan-chi/hoa-hong-thang', than, khoa),
  tienLoi: (thang: string) => goi<TienLoi>(`/v1/admin/tien-loi?thang=${thang}`),
}

const goiViApi = {
  goiViKhach: (customerId: string) => goi<GoiViCuaKhach>(`/v1/admin/khach/${customerId}/goi-vi`),
  /** STT 2.1: thêm một khách (trùng số → ApiFailure.khachTrung). Khoá chống lặp giữ suốt lần mở tờ. */
  themKhach: (than: { ten: string; sdt: string | null; luuY: string | null }, khoa: string) =>
    gui<KhachMoi>('/v1/admin/khach', than, khoa),
  /** STT 2.1: sửa tên / số / lưu ý — chỉ gửi trường đã đổi (đặt giá trị, gửi lại hai lần ra cùng kết quả). */
  suaKhach: (
    customerId: string,
    than: { ten?: string; sdt?: string | null; luuY?: string | null },
  ) =>
    goi<{ ok: boolean; sdtDaDoi: boolean }>(`/v1/admin/khach/${customerId}/sua`, {
      method: 'POST',
      body: JSON.stringify(than),
    }),
  /**
   * STT 2.2: xem trước (`thu: true`, không ghi) / nhập danh sách khách (`thu: false`, cần khoá chống lặp). Chỉ chủ
   * tiệm. Ô thô — máy chủ kiểm lại toàn bộ; không bao giờ ghi danh sách vào log / bộ nhớ trình duyệt.
   */
  nhapKhach: (than: { thu: boolean; dong: DongTho[] }, khoa?: string) =>
    goi<KetQuaNhapKhach>(
      '/v1/admin/khach/nhap',
      {
        method: 'POST',
        ...(khoa === undefined ? {} : { headers: { 'idempotency-key': khoa } }),
        body: JSON.stringify(than),
      },
      GIOI_HAN_TAI_LEN_MS,
    ),
  /** STT 2.5: ≤ 5 hồ sơ có thể trùng với khách này (chỉ chủ tiệm + quản lý). */
  goiYGop: (customerId: string) =>
    goi<{ khach: UngVienTrung[] }>(`/v1/admin/khach/${customerId}/goi-y-gop`),
  /** STT 2.5: xem trước hai chiều gộp (không ghi). */
  xemTruocGop: (a: string, b: string) =>
    goi<XemTruocGop>(`/v1/admin/khach/gop/xem-truoc?a=${a}&b=${b}`),
  /** STT 2.5: gộp. Khoá chống lặp giữ theo lựa chọn (cùng khoá khi bấm lại sau lỗi mạng). */
  gopKhach: (
    than: { giuId: string; boId: string; soGiu: 'giu' | 'bo' | null; daXacNhanZalo?: boolean },
    khoa: string,
  ) => gui<KetQuaGop>('/v1/admin/khach/gop', than, khoa),
  goiMauDangBan: () => goi<{ goi: GoiMau[] }>('/v1/admin/goi-mau'),
  banGoi: (
    customerId: string,
    than: { planId: string; branchId: string; hinhThuc: HinhThucThu },
    khoa: string,
  ) => gui<{ goi: GoiKhach }>(`/v1/admin/khach/${customerId}/goi`, than, khoa),
  xemTruocNap: (soTienVnd: string) =>
    goi<{ bonusVnd: string; bonusBp: number }>(`/v1/admin/vi/xem-truoc-nap?soTien=${soTienVnd}`),
  napVi: (
    customerId: string,
    than: { soTienVnd: string; branchId: string; hinhThuc: HinhThucThu },
    khoa: string,
  ) => gui<{ tangVnd: string; vi: ViKhach }>(`/v1/admin/khach/${customerId}/vi/nap`, than, khoa),
  /** KH-GĐ2 5.1: mã VietQR của tiệm cho phần khách chuyển khoản lúc thu (chỉ hiện, không ghi tiền). */
  qrThu: (appointmentId: string, soTienVnd: string) =>
    goi<{
      qr: string | null
      soTienVnd?: string
      nganHang?: string | null
      soTaiKhoan?: string
      tenTaiKhoan?: string
      noiDung?: string
    }>(`/v1/admin/appointments/${appointmentId}/qr-thu`, {
      method: 'POST',
      body: JSON.stringify({ soTienVnd }),
    }),
  goiPhuHop: (appointmentId: string) =>
    goi<GoiPhuHop>(`/v1/admin/appointments/${appointmentId}/goi-phu-hop`),
  hoanTat: (
    appointmentId: string,
    /** `dungVi`: có trừ ví phần còn phải trả không — MÁY CHỦ tính số tiền (money-auditor B5). */
    than: {
      dungGoi: { itemId: string; packageId: string }[]
      dungVi: boolean
      /** Pha C: thu tiền tại quầy → hoá đơn (ADR-043). */
      thuTien?: {
        hinhThuc: HinhThucThu
        giamVnd: string
        lyDoGiam: string | null
        /** Số quầy thấy phải thu — máy chủ tính lệch thì 409, không ghi gì. */
        duKienVnd?: string
        /** Pha D: sản phẩm bán kèm. */
        sanPham?: { productId: string; qty: number }[]
        /** Pha E: số điểm đổi lấy giảm giá. */
        doiDiem?: number
        /** KH-GĐ2 5.1: chia phần trả tại quầy — tổng phải bằng số máy chủ tính. */
        chia?: { hinhThuc: HinhThucThu; soTienVnd: string }[]
        /** KH-GĐ2 5.2: khách bo thợ. */
        tienBo?: TienBoGui
        /** KH-GĐ2 GĐ3: áp ưu đãi khách mới — chỉ id, máy chủ tự tính số giảm. */
        uuDaiId?: string
        /** KH-GĐ2 6.2.4: mã chủ tiệm duyệt giảm vượt trần (chỉ gửi sau khi máy chủ trả CAN_CHU_DUYET). */
        xacNhanChu?: { ma: string; giamVnd: string }
      }
    },
    khoa: string,
  ) =>
    gui<{
      status: string
      truViVnd: string
      vi: ViKhach | null
      hoaDon: HoaDonMoi | null
    }>(`/v1/admin/appointments/${appointmentId}/hoan-tat`, than, khoa),
  xemTruocHoan: (packageId: string) =>
    goi<{ toiDaVnd: string; daDung: number; giaLeVnd: string }>(
      `/v1/admin/goi/${packageId}/xem-truoc-hoan`,
    ),
  hoanGoi: (
    packageId: string,
    than: { refundVnd?: string; note: string; branchId: string; hinhThuc: HinhThucThu },
    khoa: string,
  ) =>
    gui<{ traKhachVnd: string; tiemGiuVnd: string }>(`/v1/admin/goi/${packageId}/hoan`, than, khoa),
  tangBuoi: (packageId: string, than: { soBuoi: number; note: string }, khoa: string) =>
    gui<{ goi: GoiKhach | null }>(`/v1/admin/goi/${packageId}/tang-buoi`, than, khoa),
  giaHan: (packageId: string, than: { denNgay: string; note: string }, khoa: string) =>
    gui<{ goi: GoiKhach | null }>(`/v1/admin/goi/${packageId}/gia-han`, than, khoa),
  hoanVi: (
    customerId: string,
    than: { branchId: string; hinhThuc: HinhThucThu; note: string },
    khoa: string,
  ) =>
    gui<{ traKhachVnd: string; boTangVnd: string }>(
      `/v1/admin/khach/${customerId}/vi/hoan`,
      than,
      khoa,
    ),
  soCai: (customerId: string, loai: 'goi' | 'vi', packageId?: string) =>
    goi<{ dong: DongSoCai[] }>(
      `/v1/admin/khach/${customerId}/so-cai?loai=${loai}` +
        (packageId === undefined ? '' : `&goi=${packageId}`),
    ),
  daoDong: (than: { loai: 'goi' | 'vi'; dongId: string; note: string }, khoa: string) =>
    gui<{ ok: boolean }>('/v1/admin/so-cai/dao', than, khoa),
  soDuCu: (customerId: string, than: ThanSoDuCu, khoa: string) =>
    gui<GoiViCuaKhach>(`/v1/admin/khach/${customerId}/so-du-cu`, than, khoa),
  goiMauCauHinh: () => goi<{ goi: GoiMau[] }>('/v1/admin/cau-hinh/goi'),
  luuGoiMau: (id: string | null, than: Omit<GoiMau, 'id' | 'dichVu' | 'giaLeVnd'>) =>
    goi<{ id: string }>(id === null ? '/v1/admin/cau-hinh/goi' : `/v1/admin/cau-hinh/goi/${id}`, {
      method: 'POST',
      body: JSON.stringify(than),
    }),
  mucTang: () => goi<{ mucTang: MucTangVi[] }>('/v1/admin/cau-hinh/muc-tang-vi'),
  luuMucTang: (cacMuc: { minTopupVnd: string; bonusBp: number }[]) =>
    goi<{ mucTang: MucTangVi[] }>('/v1/admin/cau-hinh/muc-tang-vi', {
      method: 'POST',
      body: JSON.stringify({ cacMuc }),
    }),
  baoCaoGoiVi: (tu: string, den: string) =>
    goi<BaoCaoGoiVi>(`/v1/admin/bao-cao/goi-vi?tu=${tu}&den=${den}`),
  huyGoiSoCu: (packageId: string, than: { note: string }, khoa: string) =>
    gui<{ goi: GoiKhach | null }>(`/v1/admin/goi/${packageId}/huy-so-cu`, than, khoa),
  nhatKyTien: () => goi<{ dong: DongNhatKyTien[] }>('/v1/admin/nhat-ky-tien'),
}

export interface DongNhatKyTien {
  id: string
  action: string
  luc: string
  nguoi: string | null
  vai: string | null
  lyDo: string | null
  /** Số tiền chính của việc: hoàn, giảm, giá trị gói sổ cũ, tiền thật nạp ví sổ cũ… */
  soTienVnd: string | null
  /** Nhập ví sổ cũ: phần tiền tặng. */
  soTienTangVnd: string | null
  /** Nhập gói sổ cũ đánh dấu "Gói tặng" (0đ, thợ không có hoa hồng). */
  tang: boolean
  /** Gói liên quan (nhập gói sổ cũ). */
  goiId: string | null
}

export interface ChiNhanhCH {
  id: string
  ten: string
  diaChi: string | null
  sdt: string | null
  hanHuyPhut: number
  /** Khách phải đặt trước ít nhất mấy phút (K5; máy chủ cũ không trả → coi như 30). */
  datTruocToiThieuPhut?: number
  /** Khách đặt trước tối đa mấy ngày (4.2; máy chủ cũ không trả → coi như 45). */
  ngayDatTruoc?: number
  dangDung: boolean
  gioLam: { thu: number; moMin: number; dongMin: number }[]
  /** K2 (ADR-111): các đợt đóng cửa từ hôm nay trở đi. Thiếu = rỗng (máy chủ cũ). */
  ngayDong?: DotDongCua[]
}

/** Một đợt đóng cửa (các ngày liên tiếp cùng lý do) kèm số lịch khách còn phải gọi đổi ngày. */
export interface DotDongCua {
  tuNgay: string
  denNgay: string
  lyDo: string | null
  soLichVuong: number
}

export interface DichVuCH {
  serviceId: string
  variantId: string
  ten: string
  /** Chuỗi số nguyên đồng (ADR-026). */
  giaVnd: string
  phut: number
  demSauPhut: number
  dangDung: boolean
  /** N3: nhắc khách quay lại sau … ngày; `null` = mặc định theo ngành (`quayLaiMacDinh`). */
  quayLaiNgay?: number | null
  quayLaiMacDinh?: number
}

export interface NhanVienCH {
  id: string
  ten: string
  vai: string
  tenVai: string
  sdt: string | null
  dangLam: boolean
  coMaDangNhap: boolean
  phaiDoiMa: boolean
  soPhienDangMo: number
  /** Đang giao — form sửa phải điền sẵn, vì lưu là THAY TOÀN BỘ. */
  chiNhanhIds: string[]
  bienTheIds: string[]
}

export interface MauCaCH {
  thu: number
  batDauMin: number
  ketThucMin: number
}

/** Các đường dẫn của màn cấu hình. Tách khỏi `createApi` vì chỉ chủ tiệm/quản lý mới gọi tới. */
export interface NgayNghi {
  id: string
  tuNgay: string
  denNgay: string
  lyDo: string | null
  /** STT 3.4: số lịch còn cần chuyển người trong khoảng nghỉ này (thiếu ở máy chủ cũ). */
  soLichVuong?: number
}

export const cauHinh = {
  chiNhanh: () => goi<{ chiNhanh: ChiNhanhCH[] }>('/v1/admin/cau-hinh/chi-nhanh'),
  luuChiNhanh: (x: {
    id?: string
    ten: string
    diaChi?: string | null
    sdt?: string | null
    hanHuyPhut?: number
    /** Thiếu = máy chủ giữ nguyên (K5). */
    datTruocToiThieuPhut?: number
    /** Thiếu = máy chủ giữ nguyên (4.2). */
    ngayDatTruoc?: number
  }) =>
    goi<{ id: string; chiNhanh: ChiNhanhCH[] }>('/v1/admin/cau-hinh/chi-nhanh', {
      method: 'POST',
      body: JSON.stringify(x),
    }),
  /** `xacNhan` (B5): đã bấm "Vẫn lưu" sau câu hỏi "tiệm sẽ không nhận được khách" — chỉ gửi cờ khi `true`. */
  luuGioLam: (
    branchId: string,
    gio: { thu: number; moMin: number; dongMin: number }[],
    xacNhan?: true,
  ) =>
    goi<{ soKhoang: number; chiNhanh: ChiNhanhCH[] } & LichVuongSauLuu>(
      `/v1/admin/cau-hinh/chi-nhanh/${branchId}/gio-lam`,
      {
        method: 'POST',
        body: JSON.stringify({ gio, ...(xacNhan === true ? { xacNhanNgungNhan: true } : {}) }),
      },
    ),
  /** K2 (ADR-111): đóng cửa cả chi nhánh từ ngày … đến ngày …; LƯU rồi trả các lịch khách còn đặt vào những ngày đó. */
  dongCua: (branchId: string, x: { tuNgay: string; denNgay: string; lyDo?: string }) =>
    goi<{ chiNhanh: ChiNhanhCH[]; soNgayMoi: number } & LichVuongSauLuu>(
      `/v1/admin/cau-hinh/chi-nhanh/${branchId}/ngay-dong`,
      { method: 'POST', body: JSON.stringify(x) },
    ),
  moLaiNgayDong: (branchId: string, x: { tuNgay: string; denNgay: string }) =>
    goi<{ chiNhanh: ChiNhanhCH[] }>(`/v1/admin/cau-hinh/chi-nhanh/${branchId}/ngay-dong/mo-lai`, {
      method: 'POST',
      body: JSON.stringify(x),
    }),

  dichVu: () => goi<{ dichVu: DichVuCH[] }>('/v1/admin/cau-hinh/dich-vu'),
  luuDichVu: (x: {
    serviceId?: string
    variantId?: string
    ten: string
    giaVnd: string
    phut: number
    demSauPhut?: number
    quayLaiNgay?: number | null
  }) =>
    goi<{ dichVu: DichVuCH[] }>('/v1/admin/cau-hinh/dich-vu', {
      method: 'POST',
      body: JSON.stringify(x),
    }),
  batTatDichVu: (serviceId: string, dung: boolean, xacNhan?: true) =>
    goi<{ dichVu: DichVuCH[] }>(`/v1/admin/cau-hinh/dich-vu/${serviceId}/bat-tat`, {
      method: 'POST',
      body: JSON.stringify({ dung, ...(xacNhan === true ? { xacNhanNgungNhan: true } : {}) }),
    }),

  nhanVien: () => goi<{ nhanVien: NhanVienCH[] }>('/v1/admin/nhan-vien'),
  /** 4 vai + quyền của từng vai theo công tắc hiện tại — màn "Ai làm được gì?". */
  vai: () =>
    goi<{ vai: { ma: string; ten: string; quyen: string[]; chiCuaMinh: string[] }[] }>(
      '/v1/admin/vai',
    ),
  luuNhanVien: (
    x: {
      id?: string
      ten: string
      sdt?: string | null
      vai: string
      chiNhanhIds?: string[]
      bienTheIds?: string[]
      /** Mã đăng nhập CỦA NGƯỜI ĐANG LÀM: đổi số của mình / chạm tới một chủ tiệm (ADR-123). */
      maXacNhan?: string
    },
    xacNhan?: true,
  ) =>
    goi<{ id: string }>('/v1/admin/cau-hinh/nhan-vien', {
      method: 'POST',
      body: JSON.stringify({ ...x, ...(xacNhan === true ? { xacNhanNgungNhan: true } : {}) }),
    }),
  /** `maXacNhan` (ADR-123): mã của người đang làm, khi cho nghỉ / bật lại một chủ tiệm. */
  batTatNhanVien: (id: string, dangLam: boolean, xacNhan?: true, maXacNhan?: string) =>
    goi<{ ok: boolean } & LichVuongSauLuu>(`/v1/admin/cau-hinh/nhan-vien/${id}/bat-tat`, {
      method: 'POST',
      body: JSON.stringify({
        dangLam,
        ...(xacNhan === true ? { xacNhanNgungNhan: true } : {}),
        ...(maXacNhan === undefined ? {} : { maXacNhan }),
      }),
    }),
  /**
   * Thu hồi mọi phiên đăng nhập của một người (mất máy / cho nghỉ việc). Chỉ chủ tiệm. `maXacNhan` (ADR-123): mã của người đang làm,
   * khi đăng xuất máy của một chủ khác hoặc "Đăng xuất các máy khác" của chính mình.
   */
  dangXuatHet: (id: string, maXacNhan?: string) =>
    goi<{ ok: boolean; soPhien: number }>(`/v1/admin/nhan-vien/${id}/dang-xuat-het`, {
      method: 'POST',
      body: JSON.stringify(maXacNhan === undefined ? {} : { maXacNhan }),
    }),
  /** `maXacNhan` (ADR-123): mã của người đang làm, khi đặt mã hộ một chủ tiệm khác. */
  datMa: (id: string, sdt: string, maXacNhan?: string) =>
    goi<{ ok: boolean; ten: string; ma: string; soPhienDaThuHoi: number }>(
      `/v1/admin/nhan-vien/${id}/dat-ma`,
      {
        method: 'POST',
        body: JSON.stringify(maXacNhan === undefined ? { sdt } : { sdt, maXacNhan }),
      },
    ),

  mauCa: (id: string) =>
    goi<{ branchId: string | null; mau: MauCaCH[] }>(`/v1/admin/cau-hinh/nhan-vien/${id}/ca`),
  /** Ngày nghỉ của KTV (F06): danh sách còn hiệu lực, cho nghỉ, huỷ. */
  ngayNghi: (id: string) => goi<{ ngayNghi: NgayNghi[] }>(`/v1/admin/nhan-vien/${id}/ngay-nghi`),
  choNghi: (id: string, x: { tuNgay: string; denNgay: string; lyDo?: string }) =>
    goi<{ id: string; lichVuong: LichVuongNghi[] }>(`/v1/admin/nhan-vien/${id}/ngay-nghi`, {
      method: 'POST',
      body: JSON.stringify(x),
    }),
  huyNghi: (ngayNghiId: string) =>
    goi<{ ok: boolean }>(`/v1/admin/ngay-nghi/${ngayNghiId}/huy`, { method: 'POST', body: '{}' }),
  luuMauCa: (id: string, chiNhanhId: string, mau: MauCaCH[], xacNhan?: true) =>
    goi<{ daXepCa: number; trungGio: number } & LichVuongSauLuu>(
      `/v1/admin/cau-hinh/nhan-vien/${id}/ca`,
      {
        method: 'POST',
        body: JSON.stringify({
          chiNhanhId,
          mau,
          ...(xacNhan === true ? { xacNhanNgungNhan: true } : {}),
        }),
      },
    ),
}

export type Api = ReturnType<typeof createApi>

/**
 * Chuẩn hoá số điện thoại Việt Nam sang dạng `+84...` mà máy chủ nhận. MỘT hàm dùng chung với máy chủ —
 * bản riêng ở đây từng chỉ đếm chữ số nên "0098 733 225" lọt qua (phản ánh 30/09).
 */
export const chuanHoaSdt: (raw: string) => string | null = chuanHoaSdtVn

/** Hiển thị số cho lễ tân đọc: `+84901234567` -> `0901 234 567`. */
/**
 * Tên khách để hiển thị. Không có tên → "Khách tới trực tiếp"; tên giả của môi trường chạy thử
 * Zalo ("User Name") → "Khách Zalo" — chữ tiếng Anh không được lọt lên màn quầy (vòng chấm UI 0).
 */
/**
 * Tên dịch vụ để hiển thị: bỏ đuôi biến thể mặc định "— Chuẩn". "Chuẩn" là ngôn ngữ của lược đồ
 * (mỗi dịch vụ có ít nhất một biến thể), không phải tên tiệm đặt cho dịch vụ.
 */
export function tenDichVu(ten: string): string {
  return ten.replace(/\s+—\s+Chuẩn$/, '')
}

export function tenKhach(ten: string | null): string {
  const sach = (ten ?? '').trim()
  if (sach === '') return 'Khách tới trực tiếp'
  if (/^user\s*name$/i.test(sach)) return 'Khách Zalo'
  return sach
}

export function hienSdt(phone: string | null): string {
  if (phone === null) return ''
  const so = phone.startsWith('+84') ? `0${phone.slice(3)}` : phone
  return /^0\d{9}$/.test(so) ? `${so.slice(0, 4)} ${so.slice(4, 7)} ${so.slice(7)}` : so
}

/*
 * Định dạng tiền và giờ đi qua `@sapa/shared` (CLAUDE.md mục 1 và 3), không tự viết lại.
 */
export {
  eachVnDate,
  formatMinuteOfDay as gioPhut,
  themNgayVn as themNgay,
  toVnDateKey,
} from '@sapa/shared'

import { formatVndChuoi, nowVn, toVnDateKey } from '@sapa/shared'

/** Hôm nay theo giờ Việt Nam, dạng `"2026-10-05"`. */
export function ngayVn(): string {
  return toVnDateKey(nowVn())
}

/**
 * Tiền từ máy chủ về dạng **chuỗi số nguyên đồng** (ADR-026), hiển thị qua `formatVnd`.
 *
 * Không dùng `toLocaleString('vi-VN')`: nó phụ thuộc dữ liệu ICU của máy chạy, và trên một
 * WebView thiếu ICU thì rơi về kiểu Anh — khách thấy "150,000đ", mà trong tiếng Việt dấu phẩy
 * là dấu thập phân. `formatVnd` tự nhóm bằng dấu chấm nên ở đâu cũng như nhau.
 */
export function tienVnd(soTien: string | number): string {
  // `formatVndChuoi` chứ không phải `formatVnd`: giao diện KHÔNG được kéo `BigInt` vào gói, vì
  // `zmp build` ép đích biên dịch xuống es2015 và một literal `0n` là vỡ cả bản dựng.
  return formatVndChuoi(typeof soTien === 'number' ? String(Math.trunc(soTien)) : soTien)
}

/**
 * Nút "bước kế tiếp" trên thẻ lịch / dòng danh sách. `nhan` (lịch chờ tiệm duyệt) đi `/duyet`, không
 * phải `/status` — `/status` không biết `nhan` và trả 400 (booking-logic-auditor N1).
 */
export function buocKe(
  api: Pick<Api, 'duyet' | 'setStatus'>,
  appointmentId: string,
  to: string,
): Promise<unknown> {
  return to === 'nhan' ? api.duyet(appointmentId, true) : api.setStatus(appointmentId, to)
}

/** Gói SAPA của tiệm (phản ánh 25/09 đợt 4–5). Chỉ chủ tiệm (`xem.goi-sapa`). */
export interface GoiSapa {
  tinhTrang: 'mo' | 'tam-ngung' | 'het-dung-thu' | 'het-han'
  /** Mã tiệm (mã đặt lịch trên QR) để ghi nội dung chuyển khoản `SAPA {mã}`. Máy chủ cũ không trả / chưa có → thiếu. */
  maTiem?: string | null
  canhBao: { muc: 'on' | 'sap-het' | 'an-han' | 'da-het' | 'tam-ngung'; ngayConLai: number | null }
  dungThuDen: string | null
  daTraDen: string | null
  /** Sổ thanh toán gói (đợt 5). Máy chủ bản cũ không trả → rỗng. */
  thanhToan: {
    id: string
    goi: string
    soTienVnd: string
    soThang: number
    tu: string
    den: string
    daDao: boolean
  }[]
}

/** Tiền tin Zalo của tiệm trong một tháng — số "đã dùng" là đúng số worker dùng để dừng gửi. */
export interface ZbsSuDung {
  thang: string
  laThangNay: boolean
  cacThang: string[]
  dangBat: boolean
  /** Tiệm tạm ngưng / hết hạn: tin tự động đã dừng dù ZBS có bật. */
  tiemDong?: boolean
  tranVnd: string | null
  tranMacDinh: boolean
  tranMacDinhVnd: string
  daTieuVnd: string
  conLaiVnd: string | null
  dung80: boolean
  het: boolean
  theoMau: { mau: string; tenVi: string; soTin: number; tienVnd: string }[]
  dem: {
    daGui: number
    dangGui: number
    boQuaNganSach: number
    boQuaDongY: number
    thatBai: number
  }
  gan: {
    luc: string
    mau: string
    tenVi: string
    trangThai: string
    tienVnd: string
    nguoiNhan: string
  }[]
}

/** 5.4: dải "SAPA cập nhật điều khoản" của chủ tiệm có sẵn. `bat: false` = cờ tắt → không hiện gì. */
export interface DieuKhoanCuaHangTrangThai {
  bat: boolean
  phienBan?: string
  ngay?: string
  duongDan?: string
  daDongY?: boolean
}
export const dieuKhoanCuaHangApi = {
  doc: () => goi<DieuKhoanCuaHangTrangThai>('/v1/admin/dieu-khoan'),
  dongY: (phienBan: string) =>
    goi<{ ok: boolean; phienBan: string }>('/v1/admin/dieu-khoan/dong-y', {
      method: 'POST',
      body: JSON.stringify({ phienBan }),
    }),
}

export const goiSapaApi = {
  doc: async (): Promise<GoiSapa> => {
    const g = await goi<Omit<GoiSapa, 'thanhToan'> & { thanhToan?: GoiSapa['thanhToan'] }>(
      '/v1/admin/goi-sapa',
    )
    return { ...g, thanhToan: g.thanhToan ?? [] }
  },
  zbs: (thang: string | null) =>
    goi<ZbsSuDung>(`/v1/admin/zbs-su-dung${thang === null ? '' : `?thang=${thang}`}`),
}

/** Nút "Góp ý" (qr-sapa-va-nut-phan-hoi.md): KHÔNG gửi tenant — tiệm và người gửi lấy từ phiên. */
export const phanHoi = {
  gui: (body: ThanGuiPhanHoi) =>
    goi<{ ok: boolean }>('/v1/admin/phan-hoi', { method: 'POST', body: JSON.stringify(body) }),
}
